import { isNumber } from 'lodash'

import type { ColorTheme } from '@marketplace-web/shared/dark-mode'
import type { TrackingEvent } from '@marketplace-web/shared/event-tracker-data'
import { TransactionSide } from '@marketplace-web/domain/conversation-messaging-data'

import { CatalogAttribute } from 'constants/catalog'
import { SortByOption } from 'constants/filter'
import { ItemBlockCtaType } from 'constants/home'
import { PayInMethodCode } from 'constants/pay-in-method'
import { PaymentsProvider } from 'constants/payments'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { ContentSource } from 'constants/tracking/content-sources'
import { ListItemContentType } from 'constants/tracking/content-types'
import { FailureReason } from 'constants/tracking/failure-reasons'
import { Filter } from 'constants/tracking/filters'
import { Screen } from 'constants/tracking/screens'
import { SavedSearchType } from '@marketplace-web/domain/search'
import { ShippingAddressScreenLocation } from 'constants/tracking/shipping-address'
import { ViewableElement } from 'constants/tracking/viewable-elements'

import type { NextScriptTrackingHeaderKey } from '@marketplace-web/domain/script-detection/server'
import type { ErrorItem } from '@marketplace-web/shared/api-client'
import { CreditCardFormLocation, CreditCardType } from 'constants/credit-card'
import { EscrowBannerTrackingType } from 'constants/tracking/escrow-banner'
import type { SelectedDynamicFilterModel } from 'types/models/dynamic-filter'
import type { FilterModel } from 'types/models/filter'
import { EscrowOrderDetailsClickTarget, EscrowOrderStatus } from 'constants/escrow-order'
import { BuyButtonPosition } from 'constants/tracking/conversation'

const nullifyEmptyValue = <T>(value: T): T | null =>
  !value ||
  (Array.isArray(value) && (value as Array<unknown>).length === 0) ||
  Object.keys(value).length === 0
    ? null
    : value

export type clickEventArgs = {
  target: ClickableElement
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: ClickableElement
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

const clickEvent = (args: clickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

export type kycClickEventArgs = {
  target: ClickableElement
  details?: string | number | null
}

const kycClickEvent = (args: kycClickEventArgs): TrackingEvent => {
  const extra: kycClickEventArgs = {
    target: args.target,
    ...(args.details ? { details: args.details } : {}),
  }

  return {
    event: 'kyc.user.click',
    extra,
  }
}

export type sddClickEventArgs = {
  target: ClickableElement
  screen?: string
  target_details?: string
}

const sddClickEvent = (args: sddClickEventArgs): TrackingEvent => {
  const extra: sddClickEventArgs = {
    target: args.target,
    ...(args.screen ? { screen: args.screen } : {}),
    ...(args.target_details ? { details: args.target_details } : {}),
  }

  return {
    event: 'sdd.user.click',
    extra,
  }
}

type FavouriteItemEventArgs = {
  itemId: number
  isFollowEvent: boolean
  contentSource?: ContentSource | null
  searchCorrelationId?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  itemOwnerId?: string | number | null
}

type FavouriteItemEventExtra = {
  item_id: number
  content_source?: ContentSource
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string
  item_owner_id?: string | number
}

const favouriteItemEvent = ({
  itemId,
  isFollowEvent,
  contentSource,
  searchCorrelationId,
  searchSessionId,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  homepageSessionId,
  itemOwnerId,
}: FavouriteItemEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.favorite_item' : 'user.unfavorite_item'
  const extra: FavouriteItemEventExtra = { item_id: itemId }

  if (contentSource) extra.content_source = contentSource
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (itemOwnerId) extra.item_owner_id = itemOwnerId

  return {
    event: eventName,
    extra,
  }
}

type followUserEventArgs = {
  userId: number
  isFollowEvent: boolean
  contentSource?: ContentSource
}

type FollowUserEventExtra = {
  user_id: number
  content_source?: ContentSource
}

const followUserEvent = ({
  userId,
  isFollowEvent,
  contentSource,
}: followUserEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.follow_user' : 'user.unfollow_user'

  const extra: FollowUserEventExtra = {
    user_id: userId,
  }

  if (isFollowEvent) extra.content_source = contentSource

  return {
    event: eventName,
    extra,
  }
}

type followBrandEventArgs = {
  brandId: number
  isFollowEvent: boolean
}

type FollowBrandEventExtra = {
  brand_id: number
}

const followBrandEvent = ({ brandId, isFollowEvent }: followBrandEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.follow_brand' : 'user.unfollow_brand'

  const extra: FollowBrandEventExtra = {
    brand_id: brandId,
  }

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

export type ClickListItemEventArgs = {
  id: number | string
  contentType: ListItemContentType
  contentSource: ContentSource
  position: number
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchScore?: string
  searchSignals?: Array<string> | null
  itemDistance?: number | null
  homepageSessionId?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

type ClickListEventExtra = {
  content_type: ListItemContentType
  content_source: ContentSource
  id: number | string
  position: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_score?: string
  search_signals?: Array<string>
  buyer_item_distance_km?: number
  homepage_session_id?: string
  screen?: Screen
  metadata?: Record<string, Array<string>>
}

const clickListItemEvent = (args: ClickListItemEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    position,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchScore,
    searchSignals,
    itemDistance,
    homepageSessionId,
    screen,
    metadata,
  } = args

  const extra: ClickListEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (searchScore) extra.search_score = searchScore
  if (searchSignals) extra.search_signals = searchSignals
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'user.click_list_item',
    extra,
  }
}

export type profileClickEventArgs = {
  target: ClickableElement
  ownerId: string
  screen?: Screen
  itemId?: string
}

type profileClickEventExtra = {
  target: ClickableElement
  owner_id: string
  screen?: Screen
  item_id?: string
}

const profileClickEvent = (args: profileClickEventArgs): TrackingEvent => {
  const { target, ownerId, screen, itemId } = args

  const extra: profileClickEventExtra = { target, owner_id: ownerId }

  if (screen) extra.screen = screen
  if (itemId) extra.item_id = itemId

  return {
    event: 'profile.click',
    extra,
  }
}

export type ClickClosetPromotionEventArgs = {
  ownerId: number
  contentSource: ContentSource
  target: string
}

type ClickClosetPromotionEventExtra = {
  owner_id: number
  content_source: ContentSource
  target: string
}

const clickClosetPromotionEvent = (args: ClickClosetPromotionEventArgs) => {
  const { contentSource, target, ownerId } = args

  const extra: ClickClosetPromotionEventExtra = {
    owner_id: ownerId,
    content_source: contentSource,
    target,
  }

  return {
    event: 'user.click_closet_promotion',
    extra,
  }
}

type endOfListEventArgs = {
  itemCount: number
  page?: number
  perPage?: number
}

type EndOfListEventExtra = {
  item_count: number
  page?: number
  per_page?: number
}

const endOfListEvent = (args: endOfListEventArgs): TrackingEvent => {
  const { itemCount, page, perPage } = args

  const extra: EndOfListEventExtra = {
    item_count: itemCount,
  }

  if (page) extra.page = page
  if (perPage) extra.per_page = perPage

  return {
    event: 'user.reach_end_of_list',
    extra,
  }
}

type viewEventArgs = {
  target: ViewableElement
  targetDetails?: string
  screen?: string
}

type ViewEventExtra = {
  target: ViewableElement
  target_details?: string
  screen?: string
}

const viewEvent = (args: viewEventArgs): TrackingEvent => {
  const { target, targetDetails, screen } = args

  const extra: ViewEventExtra = {
    target,
  }

  if (targetDetails) extra.target_details = targetDetails
  if (screen) extra.screen = screen

  return {
    event: 'user.view',
    extra,
  }
}

type viewScreenEventArgs = {
  screen: string
}

const viewScreenEvent = (args: viewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type viewUserEventArgs = {
  path: string
  user_id: number
}

const viewUserEvent = (args: viewUserEventArgs): TrackingEvent => {
  const { path, user_id } = args

  const extra = {
    path,
    user_id,
  }

  return {
    event: 'user.view_user',
    extra,
  }
}

type kycViewScreenEventArgs = {
  screen: string
  details?: string | number | null
}

const kycViewScreenEvent = (args: kycViewScreenEventArgs): TrackingEvent => {
  const extra: kycViewScreenEventArgs = {
    screen: args.screen,
    ...(args.details ? { details: args.details } : {}),
  }

  return {
    event: 'kyc.user.view_screen',
    extra,
  }
}

type sddViewScreenEventArgs = {
  screen: string
  details?: string | number | null
}

const sddViewScreenEvent = (args: sddViewScreenEventArgs): TrackingEvent => {
  const extra: sddViewScreenEventArgs = {
    screen: args.screen,
    ...(args.details ? { details: args.details } : {}),
  }

  return {
    event: 'sdd.user.view_screen',
    extra,
  }
}

type conversationReplyEventArgs = {
  userId: number
  itemId: number | null
}

const conversationReplyEvent = (args: conversationReplyEventArgs): TrackingEvent => {
  const { itemId, userId } = args

  const extra = {
    user_id: userId,
    item_id: itemId,
  }

  return {
    event: 'user.reply',
    extra,
  }
}

export type SelectSearchSuggestionEventArgs = {
  suggestions: Array<string>
  index: number
  suggestion: string
  subtitle?: string
  query: string
  countryCode: string | undefined
  searchSessionId: string
  globalSearchSessionId: string | null
  suggestionsSessionId: string
  params?: Array<Map<string, Array<string>>>
  isFrontendGeneratedSuggestion: boolean
  suggestionsListId?: string | null
  suggestionPosition?: number
}

type SelectSearchSuggestionEventExtra = {
  suggestion_index: number
  suggestions_list: Array<string>
  selected_suggestion_text: string
  selected_suggestion_subtitle?: string
  query: string
  country_code?: string
  search_session_id?: string
  global_search_session_id: string | null
  suggestions_session_id: string
  params?: Array<Map<string, Array<string>>>
  frontend_generated_text_selected?: boolean
  suggestions_list_id?: string | null
  suggestion_position?: number
}

const selectSearchSuggestionEvent = (args: SelectSearchSuggestionEventArgs): TrackingEvent => {
  const {
    suggestions,
    index,
    suggestion,
    subtitle,
    query,
    countryCode,
    searchSessionId,
    globalSearchSessionId,
    suggestionsSessionId,
    params,
    isFrontendGeneratedSuggestion,
    suggestionsListId,
    suggestionPosition,
  } = args

  const extra: SelectSearchSuggestionEventExtra = {
    suggestions_session_id: suggestionsSessionId,
    suggestion_index: index,
    suggestions_list: suggestions,
    selected_suggestion_text: suggestion,
    query,
    global_search_session_id: globalSearchSessionId,
    frontend_generated_text_selected: isFrontendGeneratedSuggestion,
  }

  if (subtitle) {
    extra.selected_suggestion_subtitle = subtitle
  }

  if (countryCode) {
    extra.country_code = countryCode
  }

  if (searchSessionId) {
    extra.search_session_id = searchSessionId
  }

  if (params?.length) {
    extra.params = params
  }

  if (suggestionsListId) {
    extra.suggestions_list_id = suggestionsListId
  }

  if (suggestionPosition) {
    extra.suggestion_position = suggestionPosition
  }

  return {
    event: 'user.select_search_suggestion',
    extra,
  }
}

export type ViewSearchSuggestionsEventArgs = {
  suggestions: Array<string>
  query: string
  suggestionsSessionId: string
}

type ViewSearchSuggestionsEventExtra = {
  suggestions_list: Array<string>
  query: string
  suggestions_session_id: string
}

const viewSearchSuggestionsEvent = (args: ViewSearchSuggestionsEventArgs): TrackingEvent => {
  const { suggestions, query, suggestionsSessionId } = args

  const extra: ViewSearchSuggestionsEventExtra = {
    suggestions_session_id: suggestionsSessionId,
    suggestions_list: suggestions,
    query,
  }

  return {
    event: 'user.view_search_suggestions',
    extra,
  }
}

export type ViewSearchSuggestionEventArgs = {
  suggestionPosition: number
  selectedSuggestionText: string
  query: string
  suggestionsSessionId: string
  suggestionsListId: string | null
  params?: Array<Map<string, Array<string>>>
  frontendGeneratedTextSelected: boolean
  searchSessionId: string | null
  globalSearchSessionId: string | null
}

type ViewSearchSuggestionEventExtra = {
  suggestion_position: number
  selected_suggestion_text: string
  query: string
  suggestions_session_id: string
  suggestions_list_id: string | null
  params?: Array<Map<string, Array<string>>>
  frontend_generated_text_selected: boolean
  search_session_id: string | null
  global_search_session_id: string | null
}

const viewSearchSuggestionEvent = (args: ViewSearchSuggestionEventArgs): TrackingEvent => {
  const {
    suggestionPosition,
    query,
    suggestionsSessionId,
    selectedSuggestionText,
    suggestionsListId,
    params,
    frontendGeneratedTextSelected,
    searchSessionId,
    globalSearchSessionId,
  } = args

  const extra: ViewSearchSuggestionEventExtra = {
    suggestion_position: suggestionPosition,
    selected_suggestion_text: selectedSuggestionText,
    query,
    suggestions_session_id: suggestionsSessionId,
    suggestions_list_id: suggestionsListId,
    frontend_generated_text_selected: frontendGeneratedTextSelected,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId,
  }

  if (params?.length) {
    extra.params = params
  }

  return {
    event: 'user.view_search_suggestion',
    extra,
  }
}

type viewShippingPriceEventArgs = {
  prices: Array<string>
  screen: string
  itemId?: number
  transactionId?: number
}

type ViewShippingPriceEventExtra = {
  prices: Array<string> | null
  screen: string
  item_id?: number
  transaction_id?: number
}

const viewShippingPriceEvent = (args: viewShippingPriceEventArgs): TrackingEvent => {
  const { prices, screen, itemId, transactionId } = args

  const extra: ViewShippingPriceEventExtra = {
    prices: prices.length ? prices : null,
    screen,
  }

  if (itemId) extra.item_id = itemId
  if (transactionId) extra.transaction_id = transactionId

  return {
    event: 'user.view_shipping_price',
    extra,
  }
}

type ClickFilterEventArgs = {
  filter: Filter
  screen?: Screen
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  type?: string
  code?: string
}

type ClickFilterEventExtra = {
  filter: Filter
  screen?: Screen
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
  type?: string
  code?: string
}

const clickFilterEvent = (args: ClickFilterEventArgs): TrackingEvent => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    type,
    code,
  } = args
  const extra: ClickFilterEventExtra = {
    filter,
    screen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (type) extra.type = type
  if (code) extra.code = code

  return {
    event: 'user.click_filter',
    extra,
  }
}

type ViewFilterEventArgs = {
  filter: Filter
  screen?: Screen
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  type?: string
  code?: string
}

type ViewFilterEventExtra = {
  filter: Filter
  screen?: Screen
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
  type?: string
  code?: string
}

const viewFilterEvent = (args: ViewFilterEventArgs): TrackingEvent => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    type,
    code,
  } = args
  const extra: ViewFilterEventExtra = {
    filter,
    screen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (type) extra.type = type
  if (code) extra.code = code

  return {
    event: 'user.view_filter',
    extra,
  }
}

type ViewSecondaryNavigationArgs = {
  catalogId: number
  position: number
  facetsCount: number
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId: string | null
}

type ViewSecondaryNavigationExtra = {
  catalog_id: number
  position: number
  facets_count: number
  search_session_id: string
  global_search_session_id: string | null
  global_catalog_browse_session_id: string | null
}

const viewSecondaryNavigationEvent = (args: ViewSecondaryNavigationArgs) => {
  const extra: ViewSecondaryNavigationExtra = {
    catalog_id: args.catalogId,
    position: args.position,
    facets_count: args.facetsCount,
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId,
    global_catalog_browse_session_id: args.globalCatalogBrowseSessionId,
  }

  return {
    event: 'user.view_secondary_navigation',
    extra,
  }
}

type ClickSecondaryNavigationArgs = {
  catalogId: number
  position: number
  facetsCount: number
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId: string | null
}

type ClickSecondaryNavigationExtra = {
  catalog_id: number
  position: number
  facets_count: number
  search_session_id: string
  global_search_session_id: string | null
  global_catalog_browse_session_id: string | null
}

const clickSecondaryNavigationEvent = (args: ClickSecondaryNavigationArgs) => {
  const extra: ClickSecondaryNavigationExtra = {
    catalog_id: args.catalogId,
    position: args.position,
    facets_count: args.facetsCount,
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId,
    global_catalog_browse_session_id: args.globalCatalogBrowseSessionId,
  }

  return {
    event: 'user.click_secondary_navigation',
    extra,
  }
}

type startConversationEventArgs = {
  userId: number
  itemId: number | string | null | undefined
}

const startConversationEvent = (args: startConversationEventArgs): TrackingEvent => {
  const { itemId, userId } = args

  const extra = {
    user_id: userId,
    item_id: itemId,
  }

  return {
    event: 'user.start_conversation',
    extra,
  }
}

type checkoutBuyerInputEventArgs = {
  transactionId?: number | null
  targetDetails?: string
  screen: string
  target: string
  valid: boolean | null
}

type CheckoutBuyerInputEventExtra = {
  valid: boolean | null
  screen: string
  target: string
  target_details?: string
  transaction_id?: number
}

const checkoutBuyerInputEvent = (args: checkoutBuyerInputEventArgs): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: CheckoutBuyerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'checkout.buyer.input',
    extra,
  }
}

type SingleCheckoutBuyerInputEventArgs = {
  valid: boolean | null
  screen: string
  target: string
  checkoutId?: string
  location?: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

type SingleCheckoutBuyerInputEventExtra = {
  valid: boolean | null
  screen: string
  target: string
  checkout_id?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  displayMode: string
}

const singleCheckoutBuyerInputEvent = (args: SingleCheckoutBuyerInputEventArgs): TrackingEvent => {
  const { checkoutId, screen, target, valid, location } = args

  const extra: SingleCheckoutBuyerInputEventExtra = {
    screen,
    target,
    valid,
    location: location || CreditCardFormLocation.Checkout,
    displayMode: 'full_screen',
  }

  if (checkoutId) extra.checkout_id = checkoutId

  return {
    event: 'credit_card_add.input',
    extra,
  }
}

type CreditCardAddModalLoadDurationEventArgs = {
  checkoutId: string
  duration: number
}

type CreditCardAddModalLoadDurationEventExtra = {
  checkout_id: string
  duration: number
  location: string
  action: string
  displayMode: string
}

const creditCardAddModalLoadDurationEvent = (
  args: CreditCardAddModalLoadDurationEventArgs,
): TrackingEvent => {
  const { checkoutId, duration } = args

  // checkoutId must be string, because hooks calling creditCardAddModalLoadDurationEvent() cannot call with undefined.
  const extra: CreditCardAddModalLoadDurationEventExtra = {
    checkout_id: checkoutId,
    location: checkoutId === '' ? CreditCardFormLocation.Settings : CreditCardFormLocation.Checkout,
    action: 'screen_load_duration_in_ms',
    duration,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.interaction',
    extra,
  }
}

type CreditCardAddModalTaskDurationEventArgs = {
  checkoutId?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type CreditCardAddModalTaskDurationEventExtra = {
  checkout_id: string
  duration: number
  location: string
  action: string
  displayMode: string
  action_details: string
}

const creditCardAddModalTaskDurationEvent = (args: CreditCardAddModalTaskDurationEventArgs) => {
  const { checkoutId, duration, utc_time_to, utc_time_from } = args

  // checkoutId can be empty '', because it is not available in /settings/payments page.
  const extra: CreditCardAddModalTaskDurationEventExtra = {
    checkout_id: checkoutId || '',
    location: checkoutId ? CreditCardFormLocation.Checkout : CreditCardFormLocation.Settings,
    displayMode: 'full_screen',
    action: 'time_on_task_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'credit_card_add.interaction',
    extra,
  }
}

type transactionSellerInputEventArgs = {
  screen: string
  target: string
  valid: boolean | null
  targetDetails?: string
  transactionId?: number | null
}

type transactionSellerInputEventExtra = {
  screen: string
  target: string
  valid: boolean | null
  target_details?: string
  transaction_id?: number
}

const transactionSellerInputEvent = (args: transactionSellerInputEventArgs): TrackingEvent => {
  const { transactionId, targetDetails, screen, target, valid } = args

  const extra: transactionSellerInputEventExtra = {
    screen,
    target,
    valid,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'transaction.seller_input',
    extra,
  }
}

type UserInputEventArgs = {
  screen?: Screen
  target: string
  value?: string | null
  state: 'focus' | 'unfocus'
  tempUuid?: string
  isDynamic?: boolean
}

const userInputEvent = (args: UserInputEventArgs): TrackingEvent => {
  const { state, target, value, screen, tempUuid, isDynamic } = args
  const extra = {
    screen,
    target,
    value,
    state,
    upload_session_id: tempUuid,
    is_dynamic: isDynamic || false,
  }

  return {
    event: 'user.input',
    extra,
  }
}

type CheckoutEventExtra = {
  transaction_id: number
  payment_method: string
  screen: string
}

type checkoutPickPaymentMethodEventArgs = {
  transactionId: number
  eventTrackingCode: string | null
  screen: string
}

const checkoutPickPaymentMethodEvent = (
  args: checkoutPickPaymentMethodEventArgs,
): TrackingEvent => {
  const { transactionId, eventTrackingCode, screen } = args

  const extra: CheckoutEventExtra = {
    transaction_id: transactionId,
    payment_method: eventTrackingCode || '',
    screen,
  }

  return {
    event: 'buyer.pick_payment_method',
    extra,
  }
}

type ClickRefundPolicyLinkEventArgs = {
  checkoutId: string
  target: ClickableElement
  screen: Screen
}

const clickRefundPolicyLinkEvent = (args: ClickRefundPolicyLinkEventArgs): TrackingEvent => {
  const { checkoutId, screen, target } = args

  return {
    event: 'checkout.click',
    extra: {
      checkout_id: checkoutId,
      screen,
      target,
    },
  }
}

type HasPaymentMethodEventArgs = {
  checkoutId: string
  screen: Screen
  capabilityType: string
  capabilityDetails: boolean
}

type HasPaymentMethodEventExtra = {
  checkout_id: string
  screen: Screen
  capability_type: string
  capability_details: boolean
}

const hasPaymentMethodEvent = (args: HasPaymentMethodEventArgs): TrackingEvent => {
  const { checkoutId, screen, capabilityType, capabilityDetails } = args
  const extra: HasPaymentMethodEventExtra = {
    checkout_id: checkoutId,
    screen,
    capability_type: capabilityType,
    capability_details: capabilityDetails,
  }

  return {
    event: 'checkout.buyer_has_capability',
    extra,
  }
}

type ViewPaymentOptionsScreenEventArgs = {
  checkoutId: string
}

const viewPaymentOptionsEvent = (args: ViewPaymentOptionsScreenEventArgs): TrackingEvent => {
  return {
    event: 'payment_options.screen',
    extra: {
      checkout_id: args.checkoutId,
      displayMode: null,
    },
  }
}

type ClickPaymentOptionsEventArgs = {
  action?: string
  checkoutId: string
  target: ClickableElement
  paymentMethodCode: PayInMethodCode
}

type ClickPaymentOptionsEventExtra = {
  action?: string
  checkout_id: string
  payment_method: string
  target: ClickableElement
  displayMode: null
}

const clickPaymentOptionsEvent = (args: ClickPaymentOptionsEventArgs): TrackingEvent => {
  const { action, target, checkoutId, paymentMethodCode } = args

  const extra: ClickPaymentOptionsEventExtra = {
    target,
    checkout_id: checkoutId,
    displayMode: null,
    payment_method: paymentMethodCode?.toLowerCase() || '',
  }

  if (action) extra.action = action

  return {
    event: 'payment_options.click',
    extra,
  }
}

type ViewCreditCardAddEventArgs = {
  screen?: string
  checkoutId?: string
  location?: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

const viewCreditCardAddEvent = (args: ViewCreditCardAddEventArgs): TrackingEvent => {
  return {
    event: 'credit_card_add.screen',
    extra: {
      screen: args.screen || Screen.CreditCardAdd,
      checkout_id: args.checkoutId || null,
      location: args.location || CreditCardFormLocation.Checkout,
      displayMode: 'full_screen',
    },
  }
}

type CreditCardAddClickEventArgs = {
  cardBrandIdentificator?: string | null
  valid?: boolean
  cardDetailsStored?: boolean
  cardBrand?: CreditCardType
  checkoutId?: string
  target: ClickableElement
  screen?: Screen
  location?: CreditCardFormLocation
  clickId?: string
}

type CreditCardAddClickEventExtra = {
  card_brand_identificator?: string | null
  target: ClickableElement
  valid?: boolean
  card_details_stored?: boolean
  card_brand?: CreditCardType
  checkout_id?: string
  screen: Screen
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  click_id?: string
  displayMode: string
}

const creditCardAddClickEvent = (args: CreditCardAddClickEventArgs) => {
  const {
    valid,
    cardDetailsStored,
    cardBrand,
    cardBrandIdentificator,
    checkoutId,
    target,
    clickId,
    location,
  } = args

  const extra: CreditCardAddClickEventExtra = {
    screen: args.screen || Screen.CreditCardAdd,
    location: location || CreditCardFormLocation.Checkout,
    card_details_stored: cardDetailsStored,
    checkout_id: checkoutId,
    target,
    valid,
    displayMode: 'full_screen',
  }

  if (clickId) extra.click_id = clickId
  if (cardBrand) extra.card_brand = cardBrand
  if (cardBrandIdentificator) extra.card_brand_identificator = cardBrandIdentificator

  return {
    event: 'credit_card_add.click',
    extra,
  }
}

type ViewPaymentSettingsEventArgs = {
  screen: Screen
}

const buyerViewSettingsCheckoutEvent = (args: ViewPaymentSettingsEventArgs) => {
  return {
    event: 'buyer.view_checkout',
    extra: {
      screen: args.screen,
      checkout_type: 'settings',
    },
  }
}

type UserViewCheckoutEventArgs = {
  screen: string
}

type UserViewCheckoutEventExtra = {
  screen: string
}

const userViewCheckoutEvent = (args: UserViewCheckoutEventArgs): TrackingEvent => {
  const { screen } = args

  const extra: UserViewCheckoutEventExtra = {
    screen,
  }

  return {
    event: 'user.view_checkout',
    extra,
  }
}

type SelectCatalogEventArgs = {
  catalogIds: Array<number>
  attributeId?: CatalogAttribute
  screen?: Screen
}

type SelectCatalogEventExtra = {
  catalog_id: string
  attribute_id: number | null
  screen?: string
}

const selectCatalogEvent = (args: SelectCatalogEventArgs): TrackingEvent => {
  const { catalogIds, attributeId, screen } = args

  const extra: SelectCatalogEventExtra = {
    catalog_id: catalogIds.join(','),
    attribute_id: attributeId ?? null,
  }

  if (screen) extra.screen = screen

  return {
    event: 'user.select_catalog',
    extra,
  }
}

type selfServiceViewScreenEventArgs = {
  screen: string
  transactionId: number | string
  faqEntryId?: number | string | null
  details?: string | null
  variant?: string | null
}

type selfServiceViewScreenEventExtra = {
  screen: string
  transaction_id: number | string
  faq_entry_id?: number | string | null
  details?: string | null
  // "variant" is not present in the schema, however it is used in some places
  variant?: string | null
}

const selfServiceViewScreenEvent = (args: selfServiceViewScreenEventArgs): TrackingEvent => {
  const { screen, transactionId, faqEntryId, details, variant } = args

  const extra: selfServiceViewScreenEventExtra = {
    screen,
    transaction_id: transactionId,
  }

  if (faqEntryId) extra.faq_entry_id = faqEntryId
  if (details) extra.details = details
  if (variant) extra.variant = variant

  return {
    event: 'user.self_service.view_screen',
    extra,
  }
}
type SelectBrandEventArgs = {
  brandId: number
  brandClusterId: number
  position: number
}

type SelectBrandEventExtra = {
  brand_id: number
  brand_cluster_id: number
  position: number
}

const selectBrandEvent = (args: SelectBrandEventArgs): TrackingEvent => {
  const { brandId, brandClusterId, position } = args

  const extra: SelectBrandEventExtra = {
    brand_id: brandId,
    brand_cluster_id: brandClusterId,
    position,
  }

  return {
    event: 'user.select_brand',
    extra,
  }
}

type UserFilterItemsEventArgs = {
  itemCount: number
  filters: FilterModel
  searchQuery: string | null
  searchCorrelationId: string
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchStartId: string | null
  searchStartType: string | null
  selectedDynamicFilters: Array<SelectedDynamicFilterModel>
}

type UserFilterItemsEventExtra = {
  item_count: number
  search_correlation_id: string
  search_session_id: string
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  conditions: Partial<{
    search_term: string | null
    brand_ids: Array<number> | null
    catalog_ids: Array<number> | null
    color_ids: Array<number> | null
    material_ids: Array<number> | null
    video_game_rating_ids: Array<number> | null
    size_ids: Array<number> | null
    status_ids: Array<number> | null
    price_from: number | null
    price_to: number | null
    order: string | null
    popular: boolean
  }>
  search_start_id?: string
  search_start_type?: string
  selected_filters?: Record<string, Array<number>>
}

const userFilterItemsEvent = (args: UserFilterItemsEventArgs): TrackingEvent => {
  const {
    itemCount,
    filters,
    searchQuery,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    searchStartId,
    searchStartType,
    selectedDynamicFilters,
  } = args

  const extra: UserFilterItemsEventExtra = {
    item_count: itemCount,
    search_correlation_id: searchCorrelationId,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    conditions: {
      search_term: searchQuery,
      catalog_ids: nullifyEmptyValue(filters.catalogIds),
      price_from: filters.priceFrom ? parseFloat(filters.priceFrom) : null,
      price_to: filters.priceTo ? parseFloat(filters.priceTo) : null,
      order: filters.sortBy ? nullifyEmptyValue(filters.sortBy) : SortByOption.Relevance,
      popular: filters.isPopularCatalog,
    },
  }

  if (searchStartType) {
    extra.search_start_type = searchStartType
  }

  if (searchStartId) {
    extra.search_start_id = searchStartId
  }

  selectedDynamicFilters
    .filter(({ ids }) => ids.length)
    .forEach(({ type, ids }) => {
      if (!extra.selected_filters) extra.selected_filters = {}

      extra.selected_filters[type] = ids
      // TO DO: remove this when dynamic catalog filters scaled
      extra.conditions[`${type}_ids`] = ids
    })

  return {
    event: 'user.filter_items',
    extra,
  }
}

type SettingsUserInputEventArgs = {
  screen: string
  target: string
  isValid: boolean
  targetDetails?: string
}

type SettingsUserInputEventExtra = {
  screen: string
  target: string
  valid: boolean
  target_details?: string
}

const settingsUserInputEvent = (args: SettingsUserInputEventArgs): TrackingEvent => {
  const { screen, target, isValid, targetDetails } = args

  const extra: SettingsUserInputEventExtra = {
    screen,
    target,
    valid: isValid,
    target_details: targetDetails,
  }

  return {
    event: 'settings.user.input',
    extra,
  }
}

type AuthType = 'internal' | 'facebook' | 'google' | 'apple'

type UserRegisterSuccessEventArgs = {
  type: AuthType
  userId?: number
}

type UserRegisterSuccessEventExtra = {
  type: AuthType
}

const userRegisterSuccessEvent = (args: UserRegisterSuccessEventArgs): TrackingEvent => {
  const { type, userId } = args

  const extra: UserRegisterSuccessEventExtra = { type }

  return { event: 'user.register_success', extra, userId }
}

type UserRegisterFailEventArgs = {
  type: AuthType
  errors?: Array<ErrorItem>
}

type UserRegisterFailEventExtra = {
  type: AuthType
  reason: string
  details?: string
}

const userRegisterFailEvent = (args: UserRegisterFailEventArgs): TrackingEvent => {
  const { type, errors } = args

  const extra: UserRegisterFailEventExtra = {
    type,
    reason: 'validation_error',
    details: errors?.map(error => error.value).join(', '),
  }

  return { event: 'user.register_fail', extra }
}

export type AuthenticateSuccessEventArgs = {
  type: AuthType
  userId?: number
}

type AuthenticateSuccessEventExtra = {
  type: AuthType
}

const authenticateSuccessEvent = (args: AuthenticateSuccessEventArgs): TrackingEvent => {
  const { type, userId } = args

  const extra: AuthenticateSuccessEventExtra = { type }

  return { event: 'user.authenticate_success', extra, userId }
}

type AuthenticateFailEventArgs = {
  type: AuthType
  errors?: Array<ErrorItem>
  error?: string
}

type AuthenticateFailEventExtra = {
  type: AuthType
  reason: string
  details?: string
}

const authenticateFailEvent = (args: AuthenticateFailEventArgs): TrackingEvent => {
  const { type, errors, error } = args

  const extra: AuthenticateFailEventExtra = {
    type,
    reason: 'validation_error',
    details: errors?.map(({ value }) => value).join(', ') || error,
  }

  return { event: 'user.authenticate_fail', extra }
}

type SelectSuggestedMessageEventArgs = {
  id: string
  position?: number
}

type SelectSuggestedMessageEventExtra = {
  id: string
  position?: number
}

const selectSuggestedMessageEvent = (args: SelectSuggestedMessageEventArgs): TrackingEvent => {
  const { id, position } = args

  const extra: SelectSuggestedMessageEventExtra = { id }
  if (position) extra.position = position

  return {
    event: 'user.click_suggested_message',
    extra,
  }
}

type ViewSuggestedMessageEventArgs = {
  id: string
  position?: number
}

type ViewSuggestedMessageEventExtra = {
  id: string
  position?: number
}

const viewSuggestedMessageEvent = (args: ViewSuggestedMessageEventArgs): TrackingEvent => {
  const { id, position } = args

  const extra: ViewSuggestedMessageEventExtra = { id }
  if (position) extra.position = position

  return {
    event: 'user.view_suggested_message',
    extra,
  }
}

type DonationsClickEventArgs = {
  target: ClickableElement
  screen?: Screen | null
  targetDetails?: string | number
}

type DonationsClickEventExtra = {
  target: string
  screen?: string
  target_details?: string | number
}

const donationsClickEvent = (args: DonationsClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails } = args

  const extra: DonationsClickEventExtra = {
    target,
  }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'user.click_donations',
    extra,
  }
}

type FundraiserClickEventArgs = {
  target: ClickableElement
  screen?: Screen | null
  targetDetails?: string | number
}

type FundraiserClickEventExtra = {
  target: string
  screen?: string
  target_details?: string | number
}

const fundraiserClickEvent = (args: FundraiserClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails } = args

  const extra: FundraiserClickEventExtra = {
    target,
  }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'user.click_donations',
    extra,
  }
}

type BuyerLoadShippingPointsEventArgs = {
  screen: string
  transactionId?: number | null
  shippingPointsCount: string | null
  latitude: number
  longitude: number
}

type BuyerLoadShippingPointsEventExtra = {
  screen: string
  transaction_id?: number | null
  shipping_points_count: string | null
  latitude: number
  longitude: number
}

const buyerLoadShippingPointsEvent = (args: BuyerLoadShippingPointsEventArgs): TrackingEvent => {
  const { transactionId, shippingPointsCount, screen, latitude, longitude } = args

  const extra: BuyerLoadShippingPointsEventExtra = {
    screen,
    latitude,
    longitude,
    shipping_points_count: shippingPointsCount,
  }

  if (transactionId) extra.transaction_id = transactionId

  return {
    event: 'buyer.load_shipping_points',
    extra,
  }
}

type BuyerPreselectedShippingOptionsEventArgs = {
  screen: Screen
  transactionId?: number | null
  distance?: number
  isNewEntry?: boolean
  carrier: string
  shippingPoint: string | null
}

type BuyerPreselectedShippingOptionsEventExtra = {
  screen: Screen
  transaction_id?: number | null
  distance?: number
  is_new_entry?: boolean
  carrier: string
  shipping_point: string | null
}

const buyerPreselectedShippingOptionsEvent = (
  args: BuyerPreselectedShippingOptionsEventArgs,
): TrackingEvent => {
  const { screen, transactionId, isNewEntry, carrier, shippingPoint, distance } = args

  const extra: BuyerPreselectedShippingOptionsEventExtra = {
    screen,
    carrier,
    shipping_point: shippingPoint,
  }

  if (transactionId) extra.transaction_id = transactionId
  if (distance !== undefined) extra.distance = distance
  if (isNewEntry) extra.is_new_entry = isNewEntry

  return {
    event: 'buyer.preselected_shipping_option',
    extra,
  }
}

type BuyerPickDeliveryTypeEventArgs = {
  transactionId?: number | null
  carrier: string | null
  deliveryType: string
  isNewEntry: boolean
  success: boolean
}

type BuyerPickDeliveryTypeEventExtra = {
  transaction_id?: number | null
  carrier: string | null
  delivery_type: string
  is_new_entry: boolean
  success: boolean
}

const buyerPickDeliveryTypeEvent = (args: BuyerPickDeliveryTypeEventArgs): TrackingEvent => {
  const { transactionId, carrier, isNewEntry, success, deliveryType } = args

  const extra: BuyerPickDeliveryTypeEventExtra = {
    success,
    carrier,
    delivery_type: deliveryType,
    is_new_entry: isNewEntry,
  }

  if (transactionId) extra.transaction_id = transactionId

  return {
    event: 'buyer.pick_delivery_type',
    extra,
  }
}

type WantItemButtonExtra =
  | 'message'
  | 'buy'
  | 'item_reservation_request'
  | 'make_offer'
  | 'create_bundle'

type BuyerWantItemEventExtra = {
  item_id: number | null
  transaction_id: number | null
  button: WantItemButtonExtra
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  search_session_id: string | null
  homepage_session_id?: string | null
}

type BuyerWantItemEventArgs = {
  transactionId: number | null
  itemId: number | null
  button: WantItemButtonExtra
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchSessionId: string | null
  homepageSessionId?: string | null
}

const buyerWantItemEvent = ({
  transactionId,
  itemId,
  button,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  searchSessionId,
  homepageSessionId,
}: BuyerWantItemEventArgs): TrackingEvent => {
  const extra: BuyerWantItemEventExtra = {
    button,
    item_id: itemId,
    transaction_id: transactionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    search_session_id: searchSessionId,
    homepage_session_id: homepageSessionId,
  }

  return {
    event: 'buyer.want_item',
    extra,
  }
}

type ConfirmWalletEventArgs = {
  source: ContentSource
  prefilled_billing_address?: boolean | null
}

type ConfirmWalletEventExtra = {
  source: string
  prefilled_billing_address?: boolean | null
}

const confirmWalletEvent = (args: ConfirmWalletEventArgs): TrackingEvent => {
  const extra: ConfirmWalletEventExtra = {
    source: args.source,
  }

  return {
    event: 'user.confirm_wallet',
    extra,
  }
}

type SaveBankAccountEventArgs = {
  valid: boolean
  details: string
  failure_reason?: FailureReason
}

type SaveBankAccountEventExtra = {
  valid: boolean
  failure_reason?: string
  details: string
}

const saveBankAccountEvent = (args: SaveBankAccountEventArgs) => {
  const extra: SaveBankAccountEventExtra = {
    valid: args.valid,
    failure_reason: args.failure_reason,
    details: args.details,
  }

  return {
    event: 'user.save_bank_account',
    extra,
  }
}

type ItemUploadClickEventArgs = {
  screen?: Screen
  target: ClickableElement
  targetDetails?: string | null
  uploadSessionId: string | null
}

type ItemUploadClickEventExtra = {
  screen?: Screen
  target: ClickableElement
  target_details?: string | null
  upload_session_id: string | null
}

const itemUploadClickEvent = (args: ItemUploadClickEventArgs): TrackingEvent => {
  const { screen, target, targetDetails, uploadSessionId } = args

  const extra: ItemUploadClickEventExtra = {
    target,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'upload_form.user.click',
    extra,
  }
}

type ItemUploadViewScreenEventArgs = {
  screen: Screen
  details?: string
  uploadSessionId: string | null
}

type ItemUploadViewScreenEventExtra = {
  screen: Screen
  details?: string
  upload_session_id: string | null
}

const itemUploadViewScreenEvent = (args: ItemUploadViewScreenEventArgs): TrackingEvent => {
  const { screen, details, uploadSessionId } = args

  const extra: ItemUploadViewScreenEventExtra = {
    screen,
    upload_session_id: uploadSessionId,
  }

  if (details) extra.details = details

  return {
    event: 'upload_form.user.view_screen',
    extra,
  }
}

type ItemUploadStartEventArgs = {
  screenName: Screen
  tempUuid?: string
}

type ItemUploadStartEventExtra = {
  upload_session_id?: string
  screen: Screen
}

const itemUploadStartEventExtra = (args: ItemUploadStartEventArgs) => {
  const extra: ItemUploadStartEventExtra = {
    screen: args.screenName,
    upload_session_id: args.tempUuid,
  }

  return {
    event: 'user.upload_item.start',
    extra,
  }
}

type ItemUploadSubmitSuccessArgs = {
  edited?: boolean
  photoCount?: number
  videoCount?: number
  itemId?: number | string
  screen?: Screen
  tempUuid?: string
}

type ItemUploadSubmitSuccessExtra = {
  edited?: boolean
  photo_count?: number
  video_count?: number
  item_id?: number | string
  screen?: Screen
  upload_session_id?: string
}

const itemUploadSubmitSuccess = (args: ItemUploadSubmitSuccessArgs) => {
  const extra: ItemUploadSubmitSuccessExtra = {
    edited: args.edited,
    photo_count: args.photoCount,
    video_count: args.videoCount,
    item_id: args.itemId,
    screen: args.screen,
    upload_session_id: args.tempUuid,
  }

  return {
    event: 'user.upload_item.submit_success',
    extra,
  }
}

const itemUploadFillZipCodeFail = () => ({
  event: 'user.upload_item.fill_zip_code_fail',
})

type ItemUploadViewSimilarSoldItemEventArgs = {
  id: number | string
  screen?: Screen
  position: number
  contentSource: ContentSource
  uploadSessionId?: string
}

type ItemUploadViewSimilarSoldItemEventExtra = {
  id: number | string
  screen?: Screen
  position: number
  content_source: ContentSource
  upload_session_id: string | undefined
}

const itemUploadViewSimilarSoldItemEvent = (args: ItemUploadViewSimilarSoldItemEventArgs) => {
  const { id, screen, position, contentSource, uploadSessionId } = args

  const extra: ItemUploadViewSimilarSoldItemEventExtra = {
    id,
    position,
    content_source: contentSource,
    upload_session_id: uploadSessionId,
  }

  if (screen) extra.screen = screen

  return { event: 'upload_form.user.view_similar_sold_item', extra }
}

type ToggleSearchSubscriptionEventArgs = {
  position?: number
  isSubscribing: boolean
  searchTitle?: string
  searchQuery?: string | null
  searchSessionId: string
  globalSearchSessionId: string | null
  screen: Screen
  savedRecentSearchListId: string | null
  savedRecentSearchSessionId: string | null
}

type ToggleSearchSubscriptionEventExtra = {
  position?: number
  is_subscribing: boolean
  search_title?: string
  search_query?: string
  search_session_id?: string
  global_search_session_id: string | null
  screen: Screen
  saved_recent_search_list_id: string | null
  saved_recent_search_session_id: string | null
}

const toggleSearchSubscriptionEvent = (args: ToggleSearchSubscriptionEventArgs) => {
  const {
    isSubscribing,
    searchTitle,
    searchQuery,
    searchSessionId,
    globalSearchSessionId,
    position,
    screen,
    savedRecentSearchListId,
    savedRecentSearchSessionId,
  } = args

  const extra: ToggleSearchSubscriptionEventExtra = {
    screen,
    position,
    saved_recent_search_session_id: savedRecentSearchSessionId,
    saved_recent_search_list_id: savedRecentSearchListId,
    is_subscribing: isSubscribing,
    global_search_session_id: globalSearchSessionId,
  }

  if (searchTitle) extra.search_title = searchTitle
  if (searchQuery) extra.search_query = searchQuery
  if (searchSessionId) extra.search_session_id = searchSessionId

  return {
    event: 'user.toggle_search_subscription',
    extra,
  }
}

type SelectSavedSearchEventArgs = {
  type: SavedSearchType
  searchTitle: string
  searchSessionId: string
  globalSearchSessionId: string | null
  newItemsCount: number | null
  unrestrictedNewItemsCount: number | null
  position: number
  screen: Screen
  savedRecentSearchListId: string
  savedRecentSearchSessionId: string
}

type SelectSavedSearchEventExtra = {
  type: SavedSearchType
  search_title: string
  search_session_id?: string
  global_search_session_id: string | null
  new_items_count?: number | null
  unrestricted_new_items_count?: number | null
  position: number
  screen: Screen
  saved_recent_search_list_id: string
  saved_recent_search_session_id: string
}

const selectSavedSearchEvent = (args: SelectSavedSearchEventArgs) => {
  const {
    type,
    searchTitle,
    searchSessionId,
    globalSearchSessionId,
    newItemsCount,
    unrestrictedNewItemsCount,
    position,
    screen,
    savedRecentSearchListId,
    savedRecentSearchSessionId,
  } = args

  const extra: SelectSavedSearchEventExtra = {
    screen,
    type,
    position,
    saved_recent_search_list_id: savedRecentSearchListId,
    new_items_count: newItemsCount,
    unrestricted_new_items_count: unrestrictedNewItemsCount,
    search_title: searchTitle,
    global_search_session_id: globalSearchSessionId,
    search_session_id: searchSessionId || undefined,
    saved_recent_search_session_id: savedRecentSearchSessionId,
  }

  return {
    event: 'user.select_saved_search',
    extra,
  }
}

const sharingReferCopyLinkEvent = (): TrackingEvent => ({
  event: 'sharing.refer.copy_link',
})

type BuyerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type BuyerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

const buyerViewAddContactDetailsEvent = (args: BuyerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: BuyerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'buyer.view_add_contact_details',
    extra,
  }
}

type SellerViewAddContactDetailsEventArgs = {
  transactionId: number
  screen: string
}

type SellerViewAddContactDetailsEventExtra = {
  transaction_id: number
  screen: string
}

const sellerViewAddContactDetailsEvent = (args: SellerViewAddContactDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: SellerViewAddContactDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'seller.view_add_contact_details',
    extra,
  }
}

type MakeOfferEventArgs = {
  isBuyer: boolean
  transactionId: number
  offerRequestId?: number
  currentPrice: number
  offeredPrice: number
  offerRequestSessionId?: string
  offerSuggestionOptionNumber: number | null
  offerSessionId?: string
  pendingSellerOfferId?: number
}

type MakeOfferEventExtra = {
  transaction_id: number
  offer_id?: number
  current_price: number
  offered_price: number
  button_number: number | null
  offer_request_session_id?: string
  offer_session_id?: string
  pending_seller_offer_id?: number
}

const makeOfferEvent = (args: MakeOfferEventArgs) => {
  const {
    isBuyer,
    currentPrice,
    offeredPrice,
    transactionId,
    offerRequestId,
    offerRequestSessionId,
    offerSuggestionOptionNumber,
    offerSessionId,
    pendingSellerOfferId,
  } = args

  const extra: MakeOfferEventExtra = {
    transaction_id: transactionId,
    offer_id: offerRequestId,
    offer_request_session_id: offerRequestSessionId,
    current_price: currentPrice,
    offered_price: offeredPrice,
    button_number: offerSuggestionOptionNumber,
    ...(offerSessionId !== undefined && { offer_session_id: offerSessionId }),
    ...(pendingSellerOfferId !== undefined && { pending_seller_offer_id: pendingSellerOfferId }),
  }

  return {
    event: isBuyer ? 'buyer.make_offer_request' : 'seller.make_offer',
    extra,
  }
}

type UpdatePriceFromConversationViewEventArgs = {
  listingId: string
  transactionId: string
  currentPrice: number
  newPrice: number
  offerRequestId: string | null
  target: string
}

const updatePriceFromConversationViewEvent = ({
  listingId,
  transactionId,
  currentPrice,
  newPrice,
  offerRequestId,
  target,
}: UpdatePriceFromConversationViewEventArgs) => {
  const extra = {
    listing_id: listingId,
    transaction_id: transactionId,
    current_price: currentPrice,
    new_price: newPrice,
    offer_request_id: offerRequestId,
    target,
  }

  return { event: 'seller.update_price_from_conversation_view', extra }
}

export enum UpdatePriceFromConversationEventAction {
  Automatic = 'automatic_on_accept',
  OnClick = 'update_price_button_click',
}

type UpdatePriceFromConversationEventArgs = {
  listingId: string
  transactionId: string
  currentPrice: number
  newPrice: number
  offerRequestId: string | null
  action: UpdatePriceFromConversationEventAction | null
}

const updatePriceFromConversationEvent = ({
  listingId,
  transactionId,
  currentPrice,
  newPrice,
  offerRequestId,
  action,
}: UpdatePriceFromConversationEventArgs) => {
  const extra = {
    listing_id: listingId,
    transaction_id: transactionId,
    current_price: currentPrice,
    new_price: newPrice,
    offer_request_id: offerRequestId,
    action,
  }

  return { event: 'seller.update_price_from_conversation', extra }
}

type SharingReferralsOpenInfoBannerLinkArgs = {
  tabName: string
}

type SharingReferralsOpenInfoBannerLinkExtra = {
  tab_name: string
}

const sharingReferralsOpenInfoBannerLink = (args: SharingReferralsOpenInfoBannerLinkArgs) => {
  const { tabName } = args

  const extra: SharingReferralsOpenInfoBannerLinkExtra = {
    tab_name: tabName,
  }

  return {
    event: 'sharing.referrals.open_info_banner_link',
    extra,
  }
}

type BuyerPrecheckoutEventArgs = {
  transactionId: number
}

type BuyerPrecheckoutEventExtra = {
  transaction_id: number
}

const buyerPrecheckoutEvent = (args: BuyerPrecheckoutEventArgs) => {
  const { transactionId } = args

  const extra: BuyerPrecheckoutEventExtra = {
    transaction_id: transactionId,
  }

  return {
    event: 'buyer.precheckout',
    extra,
  }
}

type UserPaginateCatalogEventArgs = {
  currentPage: number
  searchCorrelationId: string
  searchSessionId: string
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
}

type UserPaginateCatalogEventExtra = {
  page: number
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
}

const userPaginateCatalogEvent = (args: UserPaginateCatalogEventArgs): TrackingEvent => {
  const {
    currentPage,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
  } = args

  const extra: UserPaginateCatalogEventExtra = {
    page: currentPage,
  }

  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId

  return {
    event: 'user.paginate_catalog',
    extra,
  }
}

type UserAppliedOrderEventArgs = {
  order: SortByOption
  searchSessionId: string
  globalSearchSessionId: string | null
}

type UserAppliedOrderEventExtra = {
  order: string
  search_session_id?: string
  global_search_session_id?: string
}

const userAppliedOrderEvent = (args: UserAppliedOrderEventArgs) => {
  const { order, searchSessionId, globalSearchSessionId } = args

  const extra: UserAppliedOrderEventExtra = {
    order,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId

  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId

  return { event: 'user.applied_order', extra }
}

type UserSelectThemeEventArgs = {
  theme: ColorTheme
}

const userSelectTheme = ({ theme }: UserSelectThemeEventArgs) => {
  const extra = { theme }

  return { event: 'user.select_theme', extra }
}

type BuyerViewDeliveryDetailsEventArgs = {
  transactionId: number
  screen: string
}

type BuyerViewDeliveryDetailsEventExtra = {
  transaction_id: number
  screen: string
}

const buyerViewDeliveryDetailsEvent = (args: BuyerViewDeliveryDetailsEventArgs) => {
  const { transactionId, screen } = args

  const extra: BuyerViewDeliveryDetailsEventExtra = {
    transaction_id: transactionId,
    screen,
  }

  return {
    event: 'buyer.view_delivery_details',
    extra,
  }
}

type BuyerBuyEventArgs = {
  transactionId: number
  screen: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string | null
  sellerOfferId?: number
  buttonPosition?: BuyButtonPosition
}

type BuyerBuyEventExtra = {
  transaction_id: number
  screen: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string | null
  seller_offer_id?: number
  button_position?: BuyButtonPosition
}

const buyerBuyEvent = (args: BuyerBuyEventArgs) => {
  const {
    transactionId,
    screen,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    sellerOfferId,
    buttonPosition,
  } = args

  const extra: BuyerBuyEventExtra = {
    transaction_id: transactionId,
    screen,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId,
    homepage_session_id: homepageSessionId,
    seller_offer_id: sellerOfferId,
    button_position: buttonPosition,
  }

  return {
    event: 'buyer.buy',
    extra,
  }
}
type SellerMakeOfferStartEventArgs = {
  offerSessionId: string
  hasSuggestedPrices: boolean
  currencyCode: string
  currentPrice: number
  transactionId?: string
}

type SellerMakeOfferStartEventExtra = {
  offer_session_id: string
  has_suggested_prices: boolean
  currency: string
  current_price: number
  transaction_id?: string
}

const sellerMakeOfferStartEvent = (args: SellerMakeOfferStartEventArgs) => {
  const { offerSessionId, hasSuggestedPrices, currencyCode, currentPrice, transactionId } = args

  const extra: SellerMakeOfferStartEventExtra = {
    offer_session_id: offerSessionId,
    has_suggested_prices: hasSuggestedPrices,
    currency: currencyCode,
    current_price: currentPrice,
    transaction_id: transactionId,
  }

  return {
    event: 'seller.make_offer_start',
    extra,
  }
}

type OpenOfferScreenEventArgs = {
  isBuyer: boolean
  currentPrice: number
  currencyCode: string
  openedFrom: string
  transactionId: number
}

type OpenOfferScreenEventExtra = {
  current_price: number
  currency: string
  opened_from: string
  transaction_id: number
}

const openOfferScreenEvent = (args: OpenOfferScreenEventArgs) => {
  const { isBuyer, currentPrice, currencyCode, openedFrom, transactionId } = args

  const extra: OpenOfferScreenEventExtra = {
    current_price: currentPrice,
    currency: currencyCode,
    opened_from: openedFrom,
    transaction_id: transactionId,
  }

  return {
    event: isBuyer ? 'buyer.open_offer_screen' : 'seller.open_offer_screen',
    extra,
  }
}

export enum CheckoutInteractionType {
  Scrolled = 'scrolled',
  ReturnedBack = 'returned_back',
  CoBrandedCard = 'co_branded_card',
  ScreenOnDuration = 'screen_on_duration',
  ScreenLoadDuration = 'screen_load_duration',
}

export enum CheckoutScrollInteractionDetailsType {
  NoScrolling = 'no_scrolling',
  PartialScrolling = 'partial_scrolling',
  ScrolledTillTheEnd = 'scrolled_till_the_end',
  NoScrollingSawCompleteScreen = 'no_scrolling_saw_complete_screen',
}

type CheckoutInteractionEventArgs = {
  screen: string
  orderId?: number
  checkoutId?: number
  transactionId?: number
  action: CheckoutInteractionType
  actionDetails?:
    | { co_branded_card: boolean }
    | { screen_load_duration: number }
    | { duration_in_milliseconds: number }
    | { type: CheckoutScrollInteractionDetailsType }
}

const checkoutInteractionEvent = (args: CheckoutInteractionEventArgs): TrackingEvent => {
  const { screen, action, actionDetails, transactionId, orderId, checkoutId } = args

  return {
    event: 'buyer.interacts_with_checkout',
    extra: {
      screen,
      action,
      order_id: orderId || null,
      checkout_id: checkoutId || null,
      transaction_id: transactionId || null,
      action_details: actionDetails ? JSON.stringify(actionDetails) : null,
    },
  }
}

type ViewSuggestedPlacesEventArgs = {
  query: string
  count: number | null
  sessionToken: string
  screen: string
}

const viewSuggestedPlacesEvent = (args: ViewSuggestedPlacesEventArgs): TrackingEvent => {
  const { query, count, sessionToken, screen } = args

  return {
    event: 'user.autocomplete_results',
    extra: {
      query,
      count,
      session_token: sessionToken,
      screen,
    },
  }
}

type SelectSuggestedPlaceEventArgs = {
  selectedAutocomplete: string
  sessionToken: string
  screen: string
}

const selectSuggestedPlaceEvent = (args: SelectSuggestedPlaceEventArgs): TrackingEvent => {
  const { selectedAutocomplete, sessionToken, screen } = args

  return {
    event: 'user.autocomplete_details',
    extra: {
      selected_autocomplete: selectedAutocomplete,
      session_token: sessionToken,
      screen,
    },
  }
}

type SizeFilterEventArgs = {
  sizeIds: Array<number>
}

type SizeFilterExtra = {
  size_ids?: Array<number>
}

const sizeFilterEvent = (args: SizeFilterEventArgs): TrackingEvent => {
  const { sizeIds } = args
  let eventName = 'user.disable_size_filter'

  const extra: SizeFilterExtra = {}

  if (sizeIds.length > 0) {
    eventName = 'user.enable_size_filter'
    extra.size_ids = sizeIds
  }

  return {
    event: eventName,
    extra,
  }
}

type UserViewItemGalleryEventArgs = {
  imagePosition: number
  imageCount: number
  itemId: number
  isLoop: boolean
  contentType: string
  galleryContainsAd: boolean
}

type UserViewItemGalleryEventExtra = {
  image_position: number
  number_item_images: number
  item_id: number
  image_view: 'fullscreen'
  is_loop: boolean
  content_type: string
  gallery_contains_ad: boolean
}

const userViewItemGallery = (args: UserViewItemGalleryEventArgs): TrackingEvent => {
  const { imagePosition, imageCount, itemId, isLoop, contentType, galleryContainsAd } = args

  const extra: UserViewItemGalleryEventExtra = {
    // This value is hardcoded, because we have gallery only in a modal form
    // hence the `fullscreen` name. In apps, they have two modes to show gallery,
    // in overlay form or in the page itself, hence they have different values here.
    image_view: 'fullscreen',
    image_position: imagePosition,
    number_item_images: imageCount,
    item_id: itemId,
    is_loop: isLoop,
    content_type: contentType,
    gallery_contains_ad: galleryContainsAd,
  }

  return {
    event: 'user.view_item_gallery',
    extra,
  }
}

type ViewDynamicFilterEventArgs = {
  id: string | number
  type: string
  code: string
  position: number
  itemsCount?: number | null
  searchText?: string
  filteringSessionId: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchSessionId?: string | null
  fromScreen: Screen
}

type ViewDynamicFilterEventExtra = {
  id: string | number
  type: string
  code: string
  position: number
  items_count?: number | null
  search_text?: string
  filtering_session_id: string | null
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_session_id?: string | null
  from_screen: string
}

const userViewDynamicFilterOptionEvent = (args: ViewDynamicFilterEventArgs): TrackingEvent => {
  const {
    id,
    type,
    position,
    itemsCount,
    searchText,
    filteringSessionId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    fromScreen,
  } = args

  const extra: ViewDynamicFilterEventExtra = {
    id,
    position,
    type,
    code: type,
    items_count: itemsCount,
    search_text: searchText,
    filtering_session_id: filteringSessionId,
    from_screen: fromScreen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId

  return {
    event: 'user.view_filter_option',
    extra,
  }
}

type ClickDynamicFilterEventArgs = {
  id: string | number
  type: string
  code: string
  position: number
  isSelected: boolean
  itemsCount?: number | null
  searchText?: string
  filteringSessionId: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  searchSessionId?: string | null
  fromScreen: Screen
}

type ClickDynamicFilterEventExtra = {
  id: string | number
  type: string
  code: string
  position: number
  is_selected: boolean
  items_count?: number | null
  search_text?: string
  filtering_session_id: string | null
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  search_session_id?: string | null
  from_screen: string
}

const userClickDynamicFilterOptionEvent = (args: ClickDynamicFilterEventArgs): TrackingEvent => {
  const {
    id,
    type,
    position,
    isSelected,
    itemsCount,
    searchText,
    filteringSessionId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    fromScreen,
  } = args

  const extra: ClickDynamicFilterEventExtra = {
    id,
    position,
    type,
    code: type,
    is_selected: isSelected,
    items_count: itemsCount,
    search_text: searchText,
    filtering_session_id: filteringSessionId,
    from_screen: fromScreen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId

  return {
    event: 'user.click_filter_option',
    extra,
  }
}

type SearchFaqEntriesEventArgs = {
  searchSessionId: string | null
  searchQuery: string
  faqEntryIds: Array<number>
  helpCenterSessionId: string
}

type SearchFaqEntriesEventExtra = {
  search_session_id: string | null
  search_query: string
  faq_entry_ids: Array<number>
  help_center_session_id: string
}

const searchFaqEntriesEvent = (args: SearchFaqEntriesEventArgs) => {
  const extra: SearchFaqEntriesEventExtra = {
    search_session_id: args.searchSessionId,
    search_query: args.searchQuery,
    faq_entry_ids: args.faqEntryIds,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.search_faq_entries',
    extra,
  }
}

type LeaveFaqEntryEventArgs = {
  faqEntryId: number
  faqAccessChannel: string | null
  initialPageScrollDepth: number
  maxPageScrollDepth: number
  totalViewTime: number
  helpCenterSessionId: string
}

type LeaveFaqEntryEventExtra = {
  faq_entry_id: number
  faq_access_channel: string | null
  initial_page_scroll_depth: number
  max_page_scroll_depth: number
  total_view_time: number
  help_center_session_id: string
}

const leaveFaqEntryEvent = (args: LeaveFaqEntryEventArgs) => {
  const extra: LeaveFaqEntryEventExtra = {
    faq_entry_id: args.faqEntryId,
    faq_access_channel: args.faqAccessChannel,
    initial_page_scroll_depth: args.initialPageScrollDepth,
    max_page_scroll_depth: args.maxPageScrollDepth,
    total_view_time: args.totalViewTime,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.leave_faq_entry',
    extra,
  }
}

type ViewFaqEntryArgs = {
  faqEntryId: number
  faqEntryChannel: string
  faqAccessChannel: string | null
  screen: string
  helpCenterSessionId: string
}

type ViewFaqEntryExtra = {
  faq_entry_id: number
  faq_access_channel: string | null
  faq_entry_channel: string
  screen: string
  help_center_session_id: string
}

const viewFaqEntry = (args: ViewFaqEntryArgs) => {
  const extra: ViewFaqEntryExtra = {
    faq_entry_id: args.faqEntryId,
    faq_entry_channel: args.faqEntryChannel,
    faq_access_channel: args.faqAccessChannel,
    screen: args.screen,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.view_faq_entry',
    extra,
  }
}

type userClickFaqRelatedQuestionEventArgs = {
  helpCenterSessionId: string | null
  initialFaqId?: number
  targetFaqId: number
  faqAccessChannel?: string
  faqEntryChannel?: string
}

const userClickFaqRelatedQuestionEvent = (
  args: userClickFaqRelatedQuestionEventArgs,
): TrackingEvent => {
  const { helpCenterSessionId, initialFaqId, targetFaqId, faqEntryChannel, faqAccessChannel } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
    initial_faq_id: initialFaqId,
    target_faq_id: targetFaqId,
    faq_entry_channel: faqEntryChannel,
    faq_access_channel: faqAccessChannel,
  }

  return {
    event: 'user.click_faq_related_question',
    extra,
  }
}

type userClickRecommendedTopicEventArgs = {
  helpCenterSessionId: string | null
}

const userClickRecommendedTopicEvent = (
  args: userClickRecommendedTopicEventArgs,
): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.click_recommended_topic',
    extra,
  }
}

type userClickHelpCenterSearchResultArgs = {
  helpCenterSessionId: string | null
}

const userClickHelpCenterSearchResult = (
  args: userClickHelpCenterSearchResultArgs,
): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.click_help_center_search_result',
    extra,
  }
}

export enum FaqFeedback {
  Positive = 'positive',
  Negative = 'negative',
}

type submitFaqFeedbackEventArgs = {
  faqId: number
  helpCenterSessionId: string | null
  faqAccessChannel?: string
  faqEntryChannel?: string
  faqFeedback: FaqFeedback
}

const userSubmitFaqFeedbackEvent = (args: submitFaqFeedbackEventArgs): TrackingEvent => {
  const { faqId, helpCenterSessionId, faqEntryChannel, faqAccessChannel, faqFeedback } = args
  const extra = {
    faq_id: faqId,
    help_center_session_id: helpCenterSessionId,
    faq_entry_channel: faqEntryChannel,
    faq_access_channel: faqAccessChannel,
    faq_feedback: faqFeedback,
  }

  return {
    event: 'user.submit_faq_feedback',
    extra,
  }
}

type userContactSupportEventArgs = {
  helpCenterSessionId: string | null
}

const userContactSupportEvent = (args: userContactSupportEventArgs): TrackingEvent => {
  const { helpCenterSessionId } = args

  const extra = {
    help_center_session_id: helpCenterSessionId,
  }

  return {
    event: 'user.contact_support',
    extra,
  }
}

export enum LiveChatBannerViewStatus {
  Available = 'available',
  Unavailable = 'unavailable',
}

type LiveChatEventsBaseArgs = {
  helpCenterSessionId: string | null
}

type LiveChatBannerViewedArgs = LiveChatEventsBaseArgs & {
  status: LiveChatBannerViewStatus
}

type LiveChatBannerViewedEventExtra = {
  live_chat_status: LiveChatBannerViewStatus
  help_center_session_id: string | null
}

const liveChatBannerViewedEvent = (args: LiveChatBannerViewedArgs) => {
  const extra: LiveChatBannerViewedEventExtra = {
    live_chat_status: args.status,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_banner_viewed',
    extra,
  }
}

type LiveChatBannerClickedEventExtra = {
  help_center_session_id: string | null
}

const liveChatBannerClickedEvent = (args: LiveChatEventsBaseArgs) => {
  const extra: LiveChatBannerClickedEventExtra = {
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_banner_clicked',
    extra,
  }
}

type LiveChatFormSubmittedEventExtra = {
  help_center_session_id: string | null
}

const liveChatFormSubmittedEvent = (args: LiveChatEventsBaseArgs) => {
  const extra: LiveChatFormSubmittedEventExtra = {
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_form_submitted',
    extra,
  }
}

type LiveChatFormViewedEventExtra = {
  help_center_session_id: string | null
}

const liveChatFormViewedEvent = (args: LiveChatEventsBaseArgs) => {
  const extra: LiveChatFormViewedEventExtra = {
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'user.live_chat_form_viewed',
    extra,
  }
}

type InboxSupportFeedbackEventsBaseArgs = {
  threadId: string
  helpCenterSessionId: string
}

type InboxActionButtonEventsArgs = InboxSupportFeedbackEventsBaseArgs & {
  supportType: string
}

const endContinueActionsViewedEvent = (args: InboxActionButtonEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    support_type: args.supportType,
  }

  return {
    event: 'support_conversation_actions.end_continue_actions_viewed',
    extra,
  }
}

const continueConversationClicked = (args: InboxActionButtonEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    support_type: args.supportType,
  }

  return {
    event: 'support_conversation_actions.continue_conversation_clicked',
    extra,
  }
}

const endConversationClicked = (args: InboxActionButtonEventsArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
    support_type: args.supportType,
  }

  return {
    event: 'support_conversation_actions.end_conversation_clicked',
    extra,
  }
}

const chatbotFeedbackClosed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.chatbot_feedback_closed',
    extra,
  }
}

const autoreplyFeedbackClosed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.auto_reply_feedback_closed',
    extra,
  }
}

const chatbotFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.chatbot_feedback_v1_viewed',
    extra,
  }
}

const autoreplyFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'automated_support_feedback.auto_reply_feedback_v1_viewed',
    extra,
  }
}

const agentSupportFeedbackClosed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'agent_support_feedback.feedback_closed',
    extra,
  }
}

const agentSupportFeedbackV1Viewed = (args: InboxSupportFeedbackEventsBaseArgs) => {
  const extra = {
    thread_id: args.threadId,
    help_center_session_id: args.helpCenterSessionId,
  }

  return {
    event: 'agent_support_feedback.feedback_v1_viewed',
    extra,
  }
}

type BuyerViewCarrierDiscountedShippingPriceEventArgs = {
  screen?: Screen
  transactionId?: number | null
}

type BuyerViewCarrierDiscountedShippingPriceEventExtra = {
  screen?: Screen
  transaction_id?: number | null
}

const buyerViewCarrierDiscountedShippingPriceEvent = (
  args: BuyerViewCarrierDiscountedShippingPriceEventArgs,
): TrackingEvent => {
  const { screen, transactionId } = args

  const extra: BuyerViewCarrierDiscountedShippingPriceEventExtra = {
    screen,
    transaction_id: transactionId,
  }

  return {
    event: 'buyer.view_carrier_discounted_shipping_price',
    extra,
  }
}
const conversationPasteEvent = (conversationId: number | string) => ({
  event: 'user.conversation_paste',
  extra: {
    conversation_id: conversationId,
  },
})

type UserModifyCarrierPreferencesEventArgs = {
  screen: Screen
  target: ClickableElement
  targetDetails: string | null
}

type UserModifyCarrierPreferencesEventExtra = {
  screen: Screen
  target: ClickableElement
  target_details: string | null
}

const userModifyCarrierPreferencesEvent = (
  args: UserModifyCarrierPreferencesEventArgs,
): TrackingEvent => {
  const { screen, target, targetDetails } = args

  const extra: UserModifyCarrierPreferencesEventExtra = {
    screen,
    target,
    target_details: targetDetails,
  }

  return {
    event: 'user.modify_carrier_preferences',
    extra,
  }
}

type UserViewHomepageBlockArgs = {
  blockName: string
  blockPosition: number
  homepageSessionId?: string
  id?: string | number | null
}

type UserViewHomepageBlockExtra = {
  block_name: string
  block_position: number
  homepage_session_id?: string
  id?: string | number | null
}

const userViewHomepageBlock = (args: UserViewHomepageBlockArgs): TrackingEvent => {
  const { blockName, blockPosition, homepageSessionId, id } = args

  const extra: UserViewHomepageBlockExtra = {
    block_name: blockName,
    block_position: blockPosition,
  }

  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (id) extra.id = id

  return {
    event: 'user.view_homepage_block',
    extra,
  }
}

type UserViewHomepageBlockCtaArgs = {
  type: ItemBlockCtaType
  blockName: string
  blockPosition: number
  homepageSessionId?: string
  id?: string | number | null
}

type UserViewHomepageBlockCtaExtra = {
  type: ItemBlockCtaType
  block_name: string
  block_position: number
  homepage_session_id?: string
  id?: string | number | null
}

const userViewHomepageBlockCta = (args: UserViewHomepageBlockCtaArgs): TrackingEvent => {
  const { type, blockName, blockPosition, homepageSessionId, id } = args

  const extra: UserViewHomepageBlockCtaExtra = {
    type,
    block_name: blockName,
    block_position: blockPosition,
  }

  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (id) extra.id = id

  return {
    event: 'user.view_homepage_block_cta',
    extra,
  }
}

type UserClickHomepageBlockCtaArgs = {
  type: ItemBlockCtaType
  blockName: string
  blockPosition: number
  homepageSessionId?: string
  id?: string | number | null
}

type UserClickHomepageBlockCtaExtra = {
  type: ItemBlockCtaType
  block_name: string
  block_position: number
  homepage_session_id?: string
  id?: string | number | null
}

const userClickHomepageBlockCta = (args: UserClickHomepageBlockCtaArgs): TrackingEvent => {
  const { type, blockName, blockPosition, homepageSessionId, id } = args

  const extra: UserClickHomepageBlockCtaExtra = {
    type,
    block_name: blockName,
    block_position: blockPosition,
  }

  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (id) extra.id = id

  return {
    event: 'user.click_homepage_block_cta',
    extra,
  }
}

type UserClickHomepageVerticalArgs = {
  homepageSessionId?: string
  target: string
}

type UserClickHomepageVerticalExtra = { target: string; homepage_session_id?: string }

const userClickHomepageVertical = (args: UserClickHomepageVerticalArgs): TrackingEvent => {
  const { target, homepageSessionId } = args
  const extra: UserClickHomepageVerticalExtra = { target }

  if (homepageSessionId) extra.homepage_session_id = homepageSessionId

  return {
    event: 'user.click_homepage_vertical',
    extra,
  }
}

export enum OfferRequestAction {
  Accept = 'accept',
  Reject = 'reject',
}

type SellerActionOfferRequestArgs = {
  action: OfferRequestAction
  transactionId: number
  currentPrice: string
  offeredPrice: string
  offerRequestId: string
}

type SellerActionOfferRequestExtra = {
  offer_id: number
  transaction_id: number
  current_price: number
  offered_price: number
}

const sellerActionOfferRequest = (args: SellerActionOfferRequestArgs): TrackingEvent => {
  const { action, transactionId, currentPrice, offeredPrice, offerRequestId } = args

  const extra: SellerActionOfferRequestExtra = {
    transaction_id: transactionId,
    current_price: Number(currentPrice),
    offered_price: Number(offeredPrice),
    offer_id: Number(offerRequestId),
  }

  return {
    event: `seller.${action}_offer_request`,
    extra,
  }
}

type CheckoutBuyerTimeOnTaskEventArgs = {
  transactionId: number
  screen: string
  duration: number
  timestamp: {
    utcTimeFrom: string
    utcTimeTo: string
  }
}

type CheckoutBuyerTimeOnTaskEventExtra = {
  transaction_id: number
  screen: string
  duration: number
  timestamp: string
}

const checkoutBuyerTimeOnTaskEvent = (args: CheckoutBuyerTimeOnTaskEventArgs) => {
  const extra: CheckoutBuyerTimeOnTaskEventExtra = {
    transaction_id: args.transactionId,
    screen: args.screen,
    duration: args.duration,
    timestamp: JSON.stringify({
      utc_time_from: args.timestamp.utcTimeFrom,
      utc_time_to: args.timestamp.utcTimeTo,
    }),
  }

  return {
    event: 'buyer.time_in_checkout',
    extra,
  }
}

type TaxpayerTimeOnTaskEventArgs = {
  screen: string
  duration: number
}

type TaxpayerTimeOnTaskEventExtra = {
  screen: string
  duration: number
}

const taxpayerTimeOnTaskEvent = (args: TaxpayerTimeOnTaskEventArgs) => {
  const extra: TaxpayerTimeOnTaskEventExtra = {
    screen: args.screen,
    duration: args.duration,
  }

  return {
    event: 'taxpayers.time_in_dac7_flow',
    extra,
  }
}

type WalletHistoryTimeOnTaskEventArgs = {
  screen: string
  duration: number
}

type WalletHistoryTimeOnTaskEventExtra = {
  screen: string
  duration: number
}

const walletHistoryTimeOnTaskEvent = (args: WalletHistoryTimeOnTaskEventArgs) => {
  const extra: WalletHistoryTimeOnTaskEventExtra = {
    screen: args.screen,
    duration: args.duration,
  }

  return {
    event: 'wallet.time_in_payments_history',
    extra,
  }
}

type TaxpayersFormForceTriggerEventArgs = {
  screen: string
}

type TaxpayersFormForceTriggerEventExtra = {
  screen: string
}

const taxpayersFormForceTriggerEvent = (args: TaxpayersFormForceTriggerEventArgs) => {
  const extra: TaxpayersFormForceTriggerEventExtra = {
    screen: args.screen,
  }

  return {
    event: 'taxpayers.form_force_trigger',
    extra,
  }
}

type PaymentOptionsTimeOnTaskEventArgs = {
  checkoutId: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type PaymentOptionsTimeOnTaskEventExtra = {
  checkout_id: string
  displayMode: null
  action: string
  duration: number
  action_details: string
}

const paymentOptionsTimeOnTaskEvent = (args: PaymentOptionsTimeOnTaskEventArgs) => {
  const { checkoutId, duration, utc_time_from, utc_time_to } = args

  const extra: PaymentOptionsTimeOnTaskEventExtra = {
    checkout_id: checkoutId,
    action: 'time_on_task_in_ms',
    duration,
    displayMode: null,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'payment_options.interaction',
    extra,
  }
}

type PaymentOptionsLoadDurationEventArgs = {
  checkout_id: string
  duration: number
}

type PaymentOptionsLoadDurationEventExtra = {
  checkout_id: string
  action: string
  duration: number
  displayMode: null
}

const paymentOptionsLoadDurationEvent = (
  args: PaymentOptionsLoadDurationEventArgs,
): TrackingEvent => {
  const extra: PaymentOptionsLoadDurationEventExtra = {
    checkout_id: args.checkout_id,
    action: 'screen_load_duration_in_ms',
    displayMode: null,
    duration: args.duration,
  }

  return {
    event: 'payment_options.interaction',
    extra,
  }
}

type UserHomepageElementArgs = {
  blockName: string
  position: number
  contentSource: ContentSource
  contentSourceId: string | null
  contentSourceLabel: string
  extraAttributes?: string
  homepageSessionId: string
  screen: Screen
}

type UserHomepageElementExtra = {
  block_name: string
  position: number
  content_source: string
  content_source_id?: string
  content_source_label: string
  extra_attributes?: string
  homepage_session_id: string
  screen: string
}

const userClickHomepageElement = (args: UserHomepageElementArgs) => {
  const extra: UserHomepageElementExtra = {
    block_name: args.blockName,
    position: args.position,
    content_source: args.contentSource,
    content_source_label: args.contentSourceLabel,
    homepage_session_id: args.homepageSessionId,
    screen: args.screen,
  }

  if (args.contentSourceId) extra.content_source_id = args.contentSourceId
  if (args.extraAttributes) extra.extra_attributes = args.extraAttributes

  return {
    event: 'user.click_homepage_element',
    extra,
  }
}

const userViewHomepageElement = (args: UserHomepageElementArgs) => {
  const extra: UserHomepageElementExtra = {
    block_name: args.blockName,
    position: args.position,
    content_source: args.contentSource,
    content_source_label: args.contentSourceLabel,
    homepage_session_id: args.homepageSessionId,
    screen: args.screen,
  }

  if (args.contentSourceId) extra.content_source_id = args.contentSourceId
  if (args.extraAttributes) extra.extra_attributes = args.extraAttributes

  return {
    event: 'user.view_homepage_element',
    extra,
  }
}

type NoticesUserEventArgs = {
  threadId: string
}

type NoticesUserEventExtra = {
  thread_id: string
}

const noticesUserOpenFaq = (args: NoticesUserEventArgs) => {
  const extra: NoticesUserEventExtra = {
    thread_id: args.threadId,
  }

  return {
    event: 'notices.user.open_faq',
    extra,
  }
}

const noticesUserOpenContactForm = (args: NoticesUserEventArgs) => {
  const extra: NoticesUserEventExtra = {
    thread_id: args.threadId,
  }

  return {
    event: 'notices.user.open_contact_form',
    extra,
  }
}

const noticesUserSubmitTicket = (args: NoticesUserEventArgs) => {
  const extra: NoticesUserEventExtra = {
    thread_id: args.threadId,
  }

  return {
    event: 'notices.user.submit_ticket',
    extra,
  }
}

type UserClickMemberArgs = {
  userId: number
  contentSource: ContentSource
}

type UserClickMemberExtra = {
  user_id: number
  content_source: ContentSource
}

const userClickMemberEvent = (args: UserClickMemberArgs): TrackingEvent => {
  const extra: UserClickMemberExtra = {
    user_id: args.userId,
    content_source: args.contentSource,
  }

  return {
    event: 'user.click_member',
    extra,
  }
}

type SingleCheckoutTokenizationOutcomeEventArgs = {
  errorMessage?: string
  checkoutId?: string
  outcome: 'success' | 'failure'
  providerName: PaymentsProvider
  clickId?: string
  location: CreditCardFormLocation
}

const singleCheckoutTokenizationOutcomeEvent = (
  args: SingleCheckoutTokenizationOutcomeEventArgs,
): TrackingEvent => {
  const { outcome, errorMessage, providerName, checkoutId, clickId } = args

  const extra = {
    outcome,
    error_message: errorMessage,
    provider_name: providerName,
    checkout_id: checkoutId || null,
    click_id: clickId,
    displayMode: 'full_screen',
    location: args.location,
  }

  return {
    event: 'credit_card_add.tokenization_outcome',
    extra,
  }
}

type singleCheckoutCreditCardAddRegistrationEventArgs = {
  clickId: string
  checkoutId?: string
  cardRegistrationId: string | null
  location: CreditCardFormLocation
}

const singleCheckoutCreditCardAddRegistrationEvent = (
  args: singleCheckoutCreditCardAddRegistrationEventArgs,
): TrackingEvent => {
  const { checkoutId, clickId, cardRegistrationId, location } = args

  return {
    event: 'credit_card_add.registration',
    extra: {
      location,
      click_id: clickId,
      checkout_id: checkoutId || null,
      card_registration_id: cardRegistrationId,
      displayMode: 'full_screen',
    },
  }
}

type DsaUserEventArgs = {
  threadId: number
}

type DsaUserEventExtra = {
  thread_id: string
}

const dsaUserOpenAppealForm = (args: DsaUserEventArgs) => {
  const extra: DsaUserEventExtra = {
    thread_id: args.threadId.toString(),
  }

  return {
    event: 'dsa.user.open_appeal_form',
    extra,
  }
}

const dsaUserOpenAppealEducation = (args: DsaUserEventArgs) => {
  const extra: DsaUserEventExtra = {
    thread_id: args.threadId.toString(),
  }

  return {
    event: 'dsa.user.open_appeal_education',
    extra,
  }
}

const dsaUserOpenAppealAgreement = (args: DsaUserEventArgs) => {
  const extra: DsaUserEventExtra = {
    thread_id: args.threadId.toString(),
  }

  return {
    event: 'dsa.user.open_appeal_agreement',
    extra,
  }
}

const dsaUserAddPhotosAppealForm = (args: DsaUserEventArgs) => {
  const extra: DsaUserEventExtra = {
    thread_id: args.threadId.toString(),
  }

  return {
    event: 'dsa.user.add_photos_appeal_form',
    extra,
  }
}

const dsaUserSubmitAppealForm = (args: DsaUserEventArgs) => {
  const extra: DsaUserEventExtra = {
    thread_id: args.threadId.toString(),
  }

  return {
    event: 'dsa.user.submit_appeal_form',
    extra,
  }
}

type DsaNtdUserEventArgs = {
  referenceId: string
}

type DsaNtdUserEventExtra = {
  reference_id: string
}

const dsaNtdUserOpenAppealForm = (args: DsaNtdUserEventArgs) => {
  const extra: DsaNtdUserEventExtra = {
    reference_id: args.referenceId,
  }

  return {
    event: 'dsa_ntd.user.open_appeal_form',
    extra,
  }
}

const dsaNtdUserSubmitAppealForm = (args: DsaNtdUserEventArgs) => {
  const extra: DsaNtdUserEventExtra = {
    reference_id: args.referenceId,
  }

  return {
    event: 'dsa_ntd.user.submit_appeal_form',
    extra,
  }
}

export type taxRulesPlacementClickEventArgs = {
  targetDetails?: string
  screen: string
}

type TaxRulesPlacementClickEventExtra = {
  target_details?: string
  screen: string
}

const taxRulesPlacementClickEvent = (args: taxRulesPlacementClickEventArgs): TrackingEvent => {
  const { targetDetails, screen } = args

  const extra: TaxRulesPlacementClickEventExtra = { target_details: targetDetails, screen }

  return {
    event: 'user.click',
    extra,
  }
}

type UserKybSuccessEventArgs = {
  businessAccountId: number
}

const userKybSuccessEvent = (args: UserKybSuccessEventArgs): TrackingEvent => {
  return {
    event: 'user.kyb_success',
    extra: {
      business_account_id: args.businessAccountId,
    },
  }
}

type LandingPageViewScreenArgs = {
  screen?: string
  referrer?: string
  path?: string
  event?: string
}

type LandingPageViewScreenExtra = {
  screen?: string
  path?: string
  referrer?: string
}

const ViewLandingPageEvent = (args: LandingPageViewScreenArgs): TrackingEvent => {
  const { screen, path, referrer } = args

  const extra: LandingPageViewScreenExtra = {
    screen,
    path,
    referrer,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type LandingPageEventsArgs = {
  referrer?: string
  event?: string
  target?: ClickableElement
  targetDetails?: string
  deviceId?: string
}

type LandingPageEventsExtra = {
  referrer?: string
  event?: string
  target?: ClickableElement
  target_details?: string
  device_id?: string
}

const landingPageEvent = (args: LandingPageEventsArgs): TrackingEvent => {
  const { referrer, target, targetDetails, event: eventName, deviceId } = args

  const extra: LandingPageEventsExtra = {}

  if (targetDetails) extra.target_details = targetDetails

  if (target) extra.target = target

  if (referrer) extra.referrer = referrer

  if (deviceId) extra.device_id = deviceId

  return {
    event: eventName || 'user.view_screen',
    extra,
  }
}

type ReferralsOpenLinkEventArgs = {
  referrerId: number
}

type ReferralsOpenLinkEventExtra = {
  referrer_id: number
}

const referralsOpenLinkEvent = (args: ReferralsOpenLinkEventArgs): TrackingEvent => {
  const { referrerId } = args

  const extra: ReferralsOpenLinkEventExtra = {
    referrer_id: referrerId,
  }

  return {
    event: 'sharing.referrals.open_link',
    extra,
  }
}

const firstTimeListerEducationOpenEvent = () => {
  return {
    event: 'user.first_time_lister_education_open',
  }
}

type FirstTimeListerEducationCloseArgs = {
  slideNumber: number
}

const firstTimeListerEducationCloseEvent = (args: FirstTimeListerEducationCloseArgs) => {
  const { slideNumber } = args

  return {
    event: 'user.first_time_lister_education_close',
    extra: {
      slide_number: slideNumber,
    },
  }
}

type ViewItemArgs = {
  itemId: number
  ownerId: number
  referrerPath?: string
  searchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
}

const viewItemEvent = (args: ViewItemArgs) => {
  const { itemId, ownerId, referrerPath, searchSessionId, globalCatalogBrowseSessionId } = args

  return {
    event: 'user.view_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId,
      global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    },
  }
}

type ViewOwnItemArgs = {
  itemId: number
  ownerId: number
  referrerPath?: string
  searchSessionId: string | null
}

const viewOwnItemEvent = (args: ViewOwnItemArgs) => {
  const { itemId, ownerId, referrerPath, searchSessionId } = args

  return {
    event: 'user.view_current_user_item',
    extra: {
      item_id: itemId,
      item_owner_id: ownerId,
      path: referrerPath,
      search_session: searchSessionId,
    },
  }
}

type DataDomeScriptAction = {
  actionType: 'blocked' | 'response_displayed' | 'response_passed' | 'response_error'
  fingerprint: string
  endpointName: string
  responseType?: string
}

const dataDomeScriptActionEvent = (args: DataDomeScriptAction) => {
  const { actionType, fingerprint, endpointName, responseType } = args

  return {
    event: 'system.datadome_script_action',
    extra: {
      action_type: actionType,
      fingerprint,
      endpoint_name: endpointName,
      response_type: responseType,
    },
  }
}

type ViewSavedSearchArgs = {
  type: SavedSearchType
  searchTitle: string
  position: number
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  newItemsCount?: number | null
  unrestrictedNewItemsCount?: number | null
  screen: Screen
  savedRecentSearchListId: string
  savedRecentSearchSessionId: string
}

type ViewSavedSearchExtra = {
  type: SavedSearchType
  search_title: string
  position: number
  search_session_id?: string | null
  global_search_session_id?: string | null
  new_items_count?: number | null
  unrestricted_new_items_count?: number | null
  screen: Screen
  saved_recent_search_list_id: string
  saved_recent_search_session_id: string
}

const viewSavedSearchEvent = (args: ViewSavedSearchArgs) => {
  const {
    position,
    searchSessionId,
    searchTitle,
    type,
    newItemsCount,
    globalSearchSessionId,
    unrestrictedNewItemsCount,
    screen,
    savedRecentSearchListId,
    savedRecentSearchSessionId,
  } = args

  const extra: ViewSavedSearchExtra = {
    type,
    position,
    screen,
    saved_recent_search_list_id: savedRecentSearchListId,
    search_title: searchTitle,
    new_items_count: newItemsCount,
    search_session_id: searchSessionId,
    global_search_session_id: globalSearchSessionId,
    unrestricted_new_items_count: unrestrictedNewItemsCount,
    saved_recent_search_session_id: savedRecentSearchSessionId,
  }

  return {
    event: 'user.view_saved_search',
    extra,
  }
}

type ClickSearchSuggestionAutofillArgs = {
  suggestionPosition: number
  suggestionText: string
  query: string
  suggestionsSessionId: string | null
  suggestionsListId: string | null
  searchSessionId: string | null
  globalSearchSessionId: string | null
}

type ClickSearchSuggestionAutofillExtra = {
  suggestion_position: number
  suggestion_text: string
  query: string
  suggestions_session_id: string | null
  suggestions_list_id: string | null
  search_session_id: string | null
  global_search_session_id: string | null
}

const clickSearchSuggestionAutofillEvent = (args: ClickSearchSuggestionAutofillArgs) => {
  const extra: ClickSearchSuggestionAutofillExtra = {
    suggestion_position: args.suggestionPosition,
    suggestion_text: args.suggestionText,
    suggestions_session_id: args.suggestionsSessionId,
    suggestions_list_id: args.suggestionsListId,
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId,
    query: args.query,
  }

  return {
    event: 'user.click_search_suggestion_autofill',
    extra,
  }
}

type SearchSuggestionAutofillTextSubmittedArgs = {
  suggestionText: string
  query: string
  suggestionsSessionId: string | null
  suggestionsListId: string | null
  params: Array<Map<string, Array<string>>> | null
}

type SearchSuggestionAutofillTextSubmittedExtra = {
  suggestion_text: string
  query: string
  suggestions_session_id: string | null
  suggestions_list_id: string | null
  params: Array<Map<string, Array<string>>> | null
}

const searchSuggestionAutofillTextSubmittedEvent = (
  args: SearchSuggestionAutofillTextSubmittedArgs,
) => {
  const extra: SearchSuggestionAutofillTextSubmittedExtra = {
    suggestions_session_id: args.suggestionsSessionId,
    suggestion_text: args.suggestionText,
    suggestions_list_id: args.suggestionsListId,
    query: args.query,
    params: args.params,
  }

  return {
    event: 'user.search_suggestion_autofill_text_submitted',
    extra,
  }
}

type SystemTimingArgs = {
  section: 'largest_contentful_paint' | 'ad_load' | 'van_ad_load' | 'van_ad_asset_load'
  duration: number
  completionState: 'succeeded'
  data?: string
}

type SystemTimingExtra = {
  section: SystemTimingArgs['section']
  /** Duration in millisecond */
  duration: number
  completion_state: 'succeeded'
  data?: string
}

const systemTimingEvent = (args: SystemTimingArgs) => {
  const { section, duration, completionState, data } = args

  const extra: SystemTimingExtra = { section, duration, completion_state: completionState, data }

  return {
    event: 'system.timing',
    extra,
  }
}

type ChangeLanguageEventArgs = {
  fromLanguage: string | null
  toLanguage: string
  screen: Screen
}

type ChangeLanguageEventExtra = {
  from_language: string | null
  to_language: string
  screen: Screen
}

const changeLanguageEvent = (args: ChangeLanguageEventArgs) => {
  const extra: ChangeLanguageEventExtra = {
    from_language: args.fromLanguage,
    to_language: args.toLanguage,
    screen: args.screen,
  }

  return {
    event: 'user.change_language',
    extra,
  }
}

type StartMakeOfferRequestEventArgs = {
  offerRequestSessionId: string
  hasSuggestedPrices: boolean
  currentPrice: number
  transactionId?: string
}

type StartMakeOfferRequestEventExtra = {
  offer_request_session_id: string
  has_suggested_prices: boolean
  current_price: number
  transaction_id?: string
}

const startMakeOfferRequestEvent = (args: StartMakeOfferRequestEventArgs) => {
  const { offerRequestSessionId, hasSuggestedPrices, currentPrice, transactionId } = args

  const extra: StartMakeOfferRequestEventExtra = {
    offer_request_session_id: offerRequestSessionId,
    has_suggested_prices: hasSuggestedPrices,
    current_price: currentPrice,
    transaction_id: transactionId,
  }

  return {
    event: 'buyer.make_offer_request_start',
    extra,
  }
}

type singleCheckoutCvvInfoEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

type singleCheckoutCvvInfoEventExtra = {
  checkout_id?: string | null
  screen: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  displayMode: string
}

const singleCheckoutCvvInfoEvent = (args: singleCheckoutCvvInfoEventArgs) => {
  const { checkoutId, location } = args

  const extra: singleCheckoutCvvInfoEventExtra = {
    checkout_id: checkoutId || null,
    screen: Screen.CvvInformation,
    location,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.screen',
    extra,
  }
}

type singleCheckoutCoBrandedCardEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  capabilityDetails: boolean
}

type singleCheckoutCoBrandedCardEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  capability_type: string
  capability_details: boolean
  displayMode: string
}

const singleCheckoutCoBrandedCardEvent = (args: singleCheckoutCoBrandedCardEventArgs) => {
  const { checkoutId, location, capabilityDetails } = args

  const extra: singleCheckoutCoBrandedCardEventExtra = {
    checkout_id: checkoutId || null,
    capability_details: capabilityDetails,
    capability_type: CheckoutInteractionType.CoBrandedCard,
    location,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.buyer_has_capability',
    extra,
  }
}

type SingleCheckoutCoBrandedCardSelectionClickEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
}

type SingleCheckoutCoBrandedCardSelectionClickEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  screen: string
  displayMode: string
}

const singleCheckoutCoBrandedCardSelectionClickEvent = (
  args: SingleCheckoutCoBrandedCardSelectionClickEventArgs,
) => {
  const { checkoutId, location } = args

  const extra: SingleCheckoutCoBrandedCardSelectionClickEventExtra = {
    checkout_id: checkoutId || null,
    screen: Screen.CoBrandedSelection,
    location,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.screen',
    extra,
  }
}

type SingleCheckoutCoBrandedInfoModalFaqEventArgs = {
  checkoutId?: string
  location: CreditCardFormLocation
}

type SingleCheckoutCoBrandedInfoModalFaqEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation.Checkout | CreditCardFormLocation.Settings
  screen: string
  target: string
  displayMode: string
}

const singleCheckoutCoBrandedInfoModalFaqEvent = (
  args: SingleCheckoutCoBrandedInfoModalFaqEventArgs,
) => {
  const { checkoutId, location } = args

  const extra: SingleCheckoutCoBrandedInfoModalFaqEventExtra = {
    location,
    checkout_id: checkoutId || null,
    screen: Screen.CoBrandedSelection,
    target: ClickableElement.CobrandedModalInfo,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.click',
    extra,
  }
}

type SingleCheckoutCoBrandedCardSelectedEventArgs = {
  checkoutId?: string
  cardBrand: string
  location: CreditCardFormLocation
}

type SingleCheckoutCoBrandedCardSelectedEventExtra = {
  checkout_id: string | null
  location: CreditCardFormLocation
  screen: string
  target: string
  card_brand: string
  displayMode: string
}

const singleCheckoutCoBrandedCardSelectedEvent = (
  args: SingleCheckoutCoBrandedCardSelectedEventArgs,
) => {
  const { checkoutId, cardBrand, location } = args

  const extra: SingleCheckoutCoBrandedCardSelectedEventExtra = {
    location,
    card_brand: cardBrand,
    checkout_id: checkoutId || null,
    screen: Screen.CoBrandedSelection,
    target: ClickableElement.CobrandedModalSelection,
    displayMode: 'full_screen',
  }

  return {
    event: 'credit_card_add.click',
    extra,
  }
}

type InboxConversationClickEventArgs = {
  conversationId: number
  lastMessageId: number | null
  pendingOfferRequestId: number | null
  isOfferRequestLastMessage: boolean
}

type InboxConversationClickEventExtra = {
  last_message_id: number | string
  message_thread_id: number
  pending_offer_request_id: number | null
  is_offer_request_last_message: boolean
  screen: Screen
}

const inboxConversationClickEvent = (args: InboxConversationClickEventArgs) => {
  const { conversationId, pendingOfferRequestId, isOfferRequestLastMessage, lastMessageId } = args

  const extra: InboxConversationClickEventExtra = {
    last_message_id: lastMessageId || '',
    message_thread_id: conversationId,
    pending_offer_request_id: pendingOfferRequestId,
    is_offer_request_last_message: isOfferRequestLastMessage,
    screen: Screen.MessageList,
  }

  return {
    event: 'inbox.conversation_click',
    extra,
  }
}

type ItemAttributesEventArgs = {
  itemId: string
  attributeCode: string
  attributeValueId?: string
  details?: string
  itemPageSessionId?: string
}

type ItemAttributesEventExtra = {
  screen: string
  item_id: string
  attribute_code: string
  attribute_value_id?: string | null
  details?: string | null
  item_page_session_id?: string | null
}

const itemAttributesClickEvent = (args: ItemAttributesEventArgs) => {
  const { itemId, attributeCode, attributeValueId, details, itemPageSessionId } = args

  const extra: ItemAttributesEventExtra = {
    screen: Screen.Item,
    item_id: itemId,
    attribute_code: attributeCode,
  }

  if (attributeValueId) extra.attribute_value_id = attributeValueId
  if (details) extra.details = details
  if (itemPageSessionId) extra.item_page_session_id = itemPageSessionId

  return {
    event: 'item_page.item_attributes.click',
    extra,
  }
}

const itemAttributesViewEvent = (args: ItemAttributesEventArgs) => {
  const { itemId, attributeCode, attributeValueId, details, itemPageSessionId } = args

  const extra: ItemAttributesEventExtra = {
    screen: Screen.Item,
    item_id: itemId,
    attribute_code: attributeCode,
  }

  if (attributeValueId) extra.attribute_value_id = attributeValueId
  if (details) extra.details = details
  if (itemPageSessionId) extra.item_page_session_id = itemPageSessionId

  return {
    event: 'item_page.item_attributes.view',
    extra,
  }
}

type UserCarrierDisableFeedbackEventArgs = {
  carrierCode: string
  carrierDisableReasonsIds: Array<number>
  comment?: string
}

type UserCarrierDisableFeedbackEventExtra = {
  screen: string
  carrier_code: string
  carrier_disable_reasons_ids: Array<number>
  comment?: string
}

const userCarrierDisableFeedbackEvent = (args: UserCarrierDisableFeedbackEventArgs) => {
  const { carrierCode, carrierDisableReasonsIds, comment } = args

  const extra: UserCarrierDisableFeedbackEventExtra = {
    screen: Screen.ShippingSettings,
    carrier_code: carrierCode,
    carrier_disable_reasons_ids: carrierDisableReasonsIds,
    comment,
  }

  return {
    event: 'user.carrier_disable_feedback',
    extra,
  }
}

type FirstTimeListerEducationPageLeaveEventArgs = {
  pageNumber: number
  goingToPageNumber: number | null
  timeSpentOnPageMs: number
  initialPageScrollDepth: number
  pageScrollDepth: number
}

type FirstTimeListerEducationPageLeaveEventExtra = {
  page_number: number
  going_to_page_number: number | null
  time_spent_on_page_ms: number
  initial_page_scroll_depth: number
  page_scroll_depth: number
  screen: Screen
}

const firstTimeListerEducationPageLeaveEvent = (
  args: FirstTimeListerEducationPageLeaveEventArgs,
) => {
  const extra: FirstTimeListerEducationPageLeaveEventExtra = {
    page_number: args.pageNumber,
    going_to_page_number: args.goingToPageNumber,
    time_spent_on_page_ms: args.timeSpentOnPageMs,
    initial_page_scroll_depth: args.initialPageScrollDepth,
    page_scroll_depth: args.pageScrollDepth,
    screen: Screen.FirstTimeListerEducation,
  }

  return {
    event: 'user.first_time_lister_education_page_leave',
    extra,
  }
}

type PartialRefundOffersClickEventArgs = {
  target: ClickableElement
  transactionId: string
  complaintId: string
  targetDetails?: string
}

type PartialRefundOffersClickEventExtra = {
  target: ClickableElement
  transaction_id: string
  complaint_id: string
  target_details?: string
}

const partialRefundOffersClickEvent = (args: PartialRefundOffersClickEventArgs) => {
  const { target, transactionId, complaintId, targetDetails } = args

  const extra: PartialRefundOffersClickEventExtra = {
    target,
    transaction_id: transactionId,
    complaint_id: complaintId,
  }

  if (targetDetails) extra.target_details = targetDetails

  return { event: 'partial_refund_offers.click', extra }
}

type ItemPageChannelViewEventArgs = {
  contentSource: ContentSource
  itemId: string
}

type ItemPageChannelViewEventExtra = {
  content_source: ContentSource
  item_id: string
}

const itemPageChannelViewEvent = (args: ItemPageChannelViewEventArgs) => {
  const { contentSource, itemId } = args

  const extra: ItemPageChannelViewEventExtra = {
    content_source: contentSource,
    item_id: itemId,
  }

  return {
    event: 'item_page.channel.view',
    extra,
  }
}

type ItemPageChannelClickEventArgs = {
  target: ClickableElement
  contentSource: ContentSource
  itemId: string
}

type ItemPageChannelClickEventExtra = {
  target: ClickableElement
  content_source: ContentSource
  item_id: string
}

const itemPageChannelClickEvent = (args: ItemPageChannelClickEventArgs) => {
  const { target, contentSource, itemId } = args

  const extra: ItemPageChannelClickEventExtra = {
    target,
    content_source: contentSource,
    item_id: itemId,
  }

  return {
    event: 'item_page.channel.click',
    extra,
  }
}

type ItemPageViewEventExtra = {
  target: ViewableElement
  item_id: string
  details?: string
  item_page_session_id?: string
}

const itemPageViewEvent = (args: ItemPageViewEventExtra) => {
  const { target, item_id, details, item_page_session_id } = args

  const extra: ItemPageViewEventExtra = {
    target,
    item_id,
  }

  if (details) extra.details = details
  if (item_page_session_id) extra.item_page_session_id = item_page_session_id

  return {
    event: 'item_page.view',
    extra,
  }
}

type SuggestedShippingPointsCountViewEventArgs = {
  checkoutId: string
  count: number
}

type SuggestedShippingPointsCountViewEventExtra = {
  checkout_id: string
  screen: string
  target: string
  count: number
}

const suggestedShippingPointsCountViewEvent = (
  args: SuggestedShippingPointsCountViewEventArgs,
): TrackingEvent => {
  const extra: SuggestedShippingPointsCountViewEventExtra = {
    checkout_id: args.checkoutId,
    screen: Screen.Checkout,
    target: 'suggested_shipping_points_count',
    count: args.count,
  }

  return { event: 'shipping_options.view', extra }
}

type ChoosePickupPointClickEventArgs = {
  checkoutId: string
  info: {
    pickup_point_id: string
    carrier_code: string
    label: Array<string>
  }
}

type ChoosePickupPointClickEventExtra = {
  checkout_id: string
  screen: string
  target: string
  info: {
    pickup_point_id: string
    carrier_code: string
    label: Array<string>
  }
}

const choosePickupPointClickEvent = (args: ChoosePickupPointClickEventArgs): TrackingEvent => {
  const extra: ChoosePickupPointClickEventExtra = {
    checkout_id: args.checkoutId,
    screen: Screen.Checkout,
    target: 'choose_pickup_point',
    info: {
      pickup_point_id: args.info.pickup_point_id,
      label: args.info.label,
      carrier_code: args.info.carrier_code,
    },
  }

  return { event: 'shipping_options.click', extra }
}

type ShippingAddressScreenViewEventArgs = {
  screen: string
  checkoutId: string
}

type ShippingAddressScreenViewEventExtra = {
  screen: string
  checkout_id: string
}

const shippingAddressScreenViewEvent = (
  args: ShippingAddressScreenViewEventArgs,
): TrackingEvent => {
  const { screen, checkoutId } = args

  const extra: ShippingAddressScreenViewEventExtra = {
    checkout_id: checkoutId,
    screen,
  }

  return { event: 'shipping_address.screen', extra }
}

type ShippingAddressInputEventArgs = {
  screen: string
  checkoutId: string
  screenLocation: ShippingAddressScreenLocation
  valid: boolean
  target: string
}

type ShippingAddressInputEventExtra = {
  screen: string
  checkout_id: string
  screen_location: string
  valid: boolean
  target: string
}

const shippingAddressInputEvent = (args: ShippingAddressInputEventArgs): TrackingEvent => {
  const { screen, checkoutId, screenLocation, valid, target } = args

  const extra: ShippingAddressInputEventExtra = {
    screen,
    checkout_id: checkoutId,
    screen_location: screenLocation,
    valid,
    target,
  }

  return { event: 'shipping_address.input', extra }
}

type ShippingAddressClickEventArgs = {
  screen: string
  checkoutId: string
  target: ClickableElement
  valid: boolean
}

type ShippingAddressClickEventExtra = {
  screen: string
  checkout_id: string
  target: string
  valid: boolean
}

const shippingAddressClickEvent = (args: ShippingAddressClickEventArgs): TrackingEvent => {
  const { screen, checkoutId, valid, target } = args

  const extra: ShippingAddressClickEventExtra = {
    screen,
    checkout_id: checkoutId,
    valid,
    target,
  }

  return { event: 'shipping_address.click', extra }
}

type ContactDetailsScreenViewEventArgs = {
  screen: string
  checkoutId: string
}

type ContactDetailsScreenViewEventExtra = {
  screen: string
  checkout_id: string
  type: string
}

const contactDetailsScreenViewEvent = (args: ContactDetailsScreenViewEventArgs): TrackingEvent => {
  const extra: ContactDetailsScreenViewEventExtra = {
    screen: args.screen,
    checkout_id: args.checkoutId,
    type: 'escrow',
  }

  return { event: 'contact_details.screen', extra }
}

type ContactDetailsClickEventArgs = {
  screen: string
  checkoutId: string
  target: ClickableElement
  valid?: boolean
  info?: string
}

type ContactDetailsClickEventExtra = {
  screen: string
  checkout_id: string
  type: string
  target: string
  valid?: boolean
  info?: string
}

const contactDetailsClickEvent = (args: ContactDetailsClickEventArgs): TrackingEvent => {
  const { screen, checkoutId, valid, target, info } = args

  const extra: ContactDetailsClickEventExtra = {
    screen,
    checkout_id: checkoutId,
    valid,
    target,
    type: 'escrow',
    info,
  }

  return { event: 'contact_details.click', extra }
}

type ContactDetailsInputEventArgs = {
  screen: string
  checkoutId: string
  valid?: boolean
  info?: string
}

type ContactDetailsInputEventExtra = {
  screen: string
  checkout_id: string
  type: string
  target: string
  valid?: boolean
  info?: string
}

const contactDetailsInputEvent = (args: ContactDetailsInputEventArgs): TrackingEvent => {
  const { screen, checkoutId, valid, info } = args

  const extra: ContactDetailsInputEventExtra = {
    screen,
    checkout_id: checkoutId,
    valid,
    target: 'phone_number',
    type: 'escrow',
    info,
  }

  return { event: 'contact_details.input', extra }
}

type ShippingScreenViewEventArgs = {
  screen: string
  checkoutId: string
}

type ShippingScreenViewEventExtra = {
  screen: string
  checkout_id: string
}

const shippingScreenViewEvent = (args: ShippingScreenViewEventArgs): TrackingEvent => {
  const extra: ShippingScreenViewEventExtra = {
    screen: args.screen,
    checkout_id: args.checkoutId,
  }

  return { event: 'shipping_options.screen', extra }
}

type ShippingClickEventArgs = {
  screen: string
  checkoutId: string
  target: ClickableElement
  valid?: boolean
  info?: string
  infoPii?: string
}

type ShippingClickEventExtra = {
  screen: string
  checkout_id: string
  target: string
  valid?: boolean
  info?: string
  info_pii?: string
}

const shippingClickEvent = (args: ShippingClickEventArgs): TrackingEvent => {
  const { screen, checkoutId, valid, target, info, infoPii } = args

  const extra: ShippingClickEventExtra = {
    screen,
    checkout_id: checkoutId,
    valid,
    target,
    info,
    info_pii: infoPii,
  }

  return { event: 'shipping_options.click', extra }
}

type ShippingMapPreselectionEventArgs = {
  checkoutId: string
  target: ClickableElement
  info?: string
  infoPii?: string
}

type ShippingMapPreselectionEventExtra = {
  checkout_id: string
  target: string
  info?: string
  info_pii?: string
}

const shippingMapPreselectionEvent = (args: ShippingMapPreselectionEventArgs): TrackingEvent => {
  const { checkoutId, target, info, infoPii } = args

  const extra: ShippingMapPreselectionEventExtra = {
    checkout_id: checkoutId,
    target,
    info,
    info_pii: infoPii,
  }

  return { event: 'shipping_options.map_preselection', extra }
}
type SustainabilityImpactViewEventArgs = {
  iconName: string
  position: number
  value: string
}

type SustainabilityImpactViewEventExtra = {
  screen: string
  icon_name: string
  position: number
  value: string
}

const sustainabilityImpactViewEvent = (args: SustainabilityImpactViewEventArgs) => {
  const { iconName, position, value } = args

  const extra: SustainabilityImpactViewEventExtra = {
    icon_name: iconName,
    position,
    value,
    screen: Screen.SellerInsights,
  }

  return {
    event: 'event_user_view_sustainability_card',
    extra,
  }
}

type shippingAddressInteractionEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type shippingAddressInteractionEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

const shippingAddressInteractionEvent = (
  args: shippingAddressInteractionEventArgs,
  isTimeOnTask: boolean,
) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: shippingAddressInteractionEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: isTimeOnTask ? 'time_on_task_in_ms' : 'screen_load_duration_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'shipping_address.interaction',
    extra,
  }
}

type shippingInteractionEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type shippingInteractionEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

const shippingInteractionEvent = (args: shippingInteractionEventArgs, isTimeOnTask: boolean) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: shippingInteractionEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: isTimeOnTask ? 'time_on_task_in_ms' : 'screen_load_duration_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'shipping_options.interaction',
    extra,
  }
}

type shippingContactInteractionEventArgs = {
  checkoutId: string
  screen?: string
  duration: number
  utc_time_from: string
  utc_time_to: string
}

type shippingContactInteractionEventExtra = {
  checkout_id: string
  screen: string
  action: string
  duration: number
  action_details: string
}

const shippingContactInteractionEvent = (
  args: shippingContactInteractionEventArgs,
  isTimeOnTask: boolean,
) => {
  const { checkoutId, screen, duration, utc_time_from, utc_time_to } = args

  const extra: shippingContactInteractionEventExtra = {
    checkout_id: checkoutId,
    screen: screen || '',
    action: isTimeOnTask ? 'time_on_task_in_ms' : 'screen_load_duration_in_ms',
    duration,
    action_details: JSON.stringify({
      utc_time_from,
      utc_time_to,
    }),
  }

  return {
    event: 'contact_details.interaction',
    extra,
  }
}

export type ScriptingDetectionHeader = {
  key: NextScriptTrackingHeaderKey
  value: string
}

type WebApiScriptingDetectionEventArgs = {
  sentHeaderProperties: Array<ScriptingDetectionHeader>
  expectedHeaderProperties: Array<ScriptingDetectionHeader>
  endpointMethod: string | undefined
  endpointUrl: string | undefined
}

type WebApiScriptingDetectionEventExtra = {
  sent_header_properties: Array<ScriptingDetectionHeader>
  expected_header_properties: Array<ScriptingDetectionHeader>
  endpoint_method: string | null
  endpoint_url: string | null
}

const scriptDetectionPropertiesEvent = ({
  sentHeaderProperties,
  expectedHeaderProperties,
  endpointMethod,
  endpointUrl,
}: WebApiScriptingDetectionEventArgs) => {
  const extra: WebApiScriptingDetectionEventExtra = {
    sent_header_properties: sentHeaderProperties,
    expected_header_properties: expectedHeaderProperties,
    endpoint_method: endpointMethod || null,
    endpoint_url: endpointUrl || null,
  }

  return {
    event: 'security.web_api_scripting_detection',
    extra,
  }
}

type ClickCatalogBackArgs = {
  searchSessionId: string | null
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId: string | null
}

type ClickCatalogBackExtra = {
  search_session_id: string | null
  global_search_session_id: string | null
  global_catalog_browse_session_id: string | null
}
const clickCatalogBack = (args: ClickCatalogBackArgs) => {
  const extra: ClickCatalogBackExtra = {
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId,
    global_catalog_browse_session_id: args.globalCatalogBrowseSessionId,
  }

  return {
    event: 'user.click_catalog_back',
    extra,
  }
}

type UserMessageThreadBannerArgs = {
  transactionId: string | null
  userId: string | null
  userSide: TransactionSide | null
  bannerCategory: string
  bannerType: EscrowBannerTrackingType | string | null
}

type UserMessageThreadBannerExtra = {
  transaction_id: string | null
  user_id: string | null
  user_side: TransactionSide | null
  banner_category: string
  banner_type: EscrowBannerTrackingType | string | null
}

const userMessageThreadBannerViewEvent = (args: UserMessageThreadBannerArgs) => {
  const extra: UserMessageThreadBannerExtra = {
    transaction_id: args.transactionId,
    user_id: args.userId,
    user_side: args.userSide,
    banner_category: args.bannerCategory,
    banner_type: args.bannerType,
  }

  return {
    event: 'user.msg_thread_banner_view',
    extra,
  }
}

const userMessageThreadBannerCloseEvent = (args: UserMessageThreadBannerArgs) => {
  const extra: UserMessageThreadBannerExtra = {
    transaction_id: args.transactionId,
    user_id: args.userId,
    user_side: args.userSide,
    banner_category: args.bannerCategory,
    banner_type: args.bannerType,
  }

  return {
    event: 'user.msg_thread_banner_close',
    extra,
  }
}

const userMessageThreadBannerClickLinkEvent = (args: UserMessageThreadBannerArgs) => {
  const extra: UserMessageThreadBannerExtra = {
    transaction_id: args.transactionId,
    user_id: args.userId,
    user_side: args.userSide,
    banner_category: args.bannerCategory,
    banner_type: args.bannerType,
  }

  return {
    event: 'user.msg_thread_banner_click_link',
    extra,
  }
}

export type ClickClaimYourGiftEventArgs = {
  screen: string
}

type ClickClaimYourGiftEventExtra = {
  screen: string
}

const clickClaimYourGiftEvent = (args: ClickClaimYourGiftEventArgs): TrackingEvent => {
  const { screen } = args

  const extra: ClickClaimYourGiftEventExtra = { screen }

  return {
    event: 'user.click_claim_your_gift',
    extra,
  }
}

export enum ReasonForNotBundling {
  ShippingSize = 'shipping_size_mismatch',
}

type CannotBundleButtonViewEventArgs = {
  listingId: string | number | null
  sellerId: string | number | null
  reasonForNotBundling: ReasonForNotBundling
  screen: string | null
  bundleSessionId: string | null
}

type CannotBundleButtonViewEventExtra = {
  listing_id: string | number | null
  seller_id: string | number | null
  reason_for_not_bundling: ReasonForNotBundling
  screen: string | null
  bundle_session_id: string | null
}

const cannotBundleButtonViewEvent = (args: CannotBundleButtonViewEventArgs): TrackingEvent => {
  const { listingId, sellerId, reasonForNotBundling, screen, bundleSessionId } = args
  const extra: CannotBundleButtonViewEventExtra = {
    listing_id: listingId,
    seller_id: sellerId,
    reason_for_not_bundling: reasonForNotBundling,
    screen,
    bundle_session_id: bundleSessionId,
  }

  return {
    event: 'bundles.cannot_bundle_button_view',
    extra,
  }
}

type CannotBundleButtonClickEventArgs = {
  listingId: string | number | null
  sellerId: string | number | null
  reasonForNotBundling: ReasonForNotBundling
  screen: string | null
  bundleSessionId: string | null
}

type CannotBundleButtonClickEventExtra = {
  listing_id: string | number | null
  seller_id: string | number | null
  reason_for_not_bundling: ReasonForNotBundling
  screen: string | null
  bundle_session_id: string | null
}

const cannotBundleButtonClickEvent = (args: CannotBundleButtonClickEventArgs): TrackingEvent => {
  const { listingId, sellerId, reasonForNotBundling, screen, bundleSessionId } = args
  const extra: CannotBundleButtonClickEventExtra = {
    listing_id: listingId,
    seller_id: sellerId,
    reason_for_not_bundling: reasonForNotBundling,
    screen,
    bundle_session_id: bundleSessionId,
  }

  return {
    event: 'bundles.cannot_bundle_button_click',
    extra,
  }
}

type CannotBundleHyperlinkClickEventArgs = {
  listingId: string | number | null
  sellerId: string | number | null
  reasonForNotBundling: ReasonForNotBundling
  screen: string | null
  bundleSessionId: string | null
}

type CannotBundleHyperlinkClickEventExtra = {
  listing_id: string | number | null
  seller_id: string | number | null
  reason_for_not_bundling: ReasonForNotBundling
  screen: string | null
  bundle_session_id: string | null
}

const cannotBundleHyperlinkClickEvent = (
  args: CannotBundleHyperlinkClickEventArgs,
): TrackingEvent => {
  const { listingId, sellerId, reasonForNotBundling, screen, bundleSessionId } = args
  const extra: CannotBundleHyperlinkClickEventExtra = {
    listing_id: listingId,
    seller_id: sellerId,
    reason_for_not_bundling: reasonForNotBundling,
    screen,
    bundle_session_id: bundleSessionId,
  }

  return {
    event: 'bundles.cannot_bundle_hyperlink_click',
    extra,
  }
}

type TaxpayersClickEventArgs = {
  screen: string
  target: 'block_modal' | 'banner' | 'hyperlink' | 'button'
  target_name: string
  target_details: string | null
}

const taxpayersClickEvent = (args: TaxpayersClickEventArgs): TrackingEvent => {
  const { screen, target, target_details, target_name } = args

  return {
    event: 'taxpayers.click',
    extra: {
      screen,
      target,
      target_details,
      target_name,
    },
  }
}

type TaxpayersInputEventArgs = {
  screen: string
  target: string
  value?: string
  state: 'focus' | 'unfocus'
}

const taxpayersInputEvent = (args: TaxpayersInputEventArgs): TrackingEvent => {
  const { screen, target, value, state } = args

  return {
    event: 'taxpayers.input',
    extra: { screen, target, value, state },
  }
}

type TaxpayersViewEventArgs = {
  screen: string
  target: 'block_modal' | 'banner'
  target_details: string
}

const taxpayersViewEvent = (args: TaxpayersViewEventArgs): TrackingEvent => {
  const { screen, target, target_details } = args

  return {
    event: 'taxpayers.view',
    extra: { screen, target, target_details },
  }
}

type TaxpayersViewScreenEventArgs = {
  screen: string
  details: string | null
}

const taxpayersViewScreenEvent = (args: TaxpayersViewScreenEventArgs): TrackingEvent => {
  const { screen, details } = args

  return {
    event: 'taxpayers.view_screen',
    extra: { screen, details },
  }
}

type EscrowOrderDetailsViewEventArgs = {
  screen: string
  escrow_order_id: string
  user_id: string
  user_side: string
  escrow_order_status: EscrowOrderStatus
  is_suspended: boolean
  action: Array<string> | null
  details: string | null
}

const escrowOrderDetailsViewEvent = (args: EscrowOrderDetailsViewEventArgs): TrackingEvent => {
  const {
    screen,
    escrow_order_id,
    user_id,
    user_side,
    escrow_order_status,
    is_suspended,
    action,
    details,
  } = args

  return {
    event: 'escrow_order_details.view',
    extra: {
      screen,
      escrow_order_id,
      user_id,
      user_side,
      escrow_order_status,
      is_suspended,
      action,
      details,
    },
  }
}

type EscrowOrderDetailsActionLatencyViewEventArgs = {
  screen: string
  escrow_order_id: string
  user_id: string
  escrow_order_status: EscrowOrderStatus
  action: Array<string> | null
  latency_ms: string
  details: string | null
}

const escrowOrderDetailsActionLatencyViewEvent = (
  args: EscrowOrderDetailsActionLatencyViewEventArgs,
): TrackingEvent => {
  const { screen, escrow_order_id, user_id, escrow_order_status, action, latency_ms, details } =
    args

  return {
    event: 'escrow_order_details.action_latency.view',
    extra: {
      screen,
      escrow_order_id,
      user_id,
      escrow_order_status,
      action,
      latency_ms,
      details,
    },
  }
}

type EscrowOrderDetailsClickEventArgs = {
  screen: string
  escrow_order_id: string
  user_id: string
  user_side: string
  escrow_order_status: EscrowOrderStatus
  is_suspended: boolean
  target: EscrowOrderDetailsClickTarget
  details: string | null
}

const escrowOrderDetailsClickEvent = (args: EscrowOrderDetailsClickEventArgs): TrackingEvent => {
  const {
    screen,
    escrow_order_id,
    user_id,
    user_side,
    escrow_order_status,
    is_suspended,
    target,
    details,
  } = args

  return {
    event: 'escrow_order_details.click',
    extra: {
      screen,
      escrow_order_id,
      user_id,
      user_side,
      escrow_order_status,
      is_suspended,
      target,
      details,
    },
  }
}

type UserReportSubmittedEventArgs = {
  entity_type: string
  entity_id: string
  reason_id: string
  photo_id: string | null
  message_id: string | null
  report_session_id: string
}

const userReportSubmittedEvent = (args: UserReportSubmittedEventArgs): TrackingEvent => {
  const { entity_type, entity_id, reason_id, photo_id, message_id, report_session_id } = args

  return {
    event: 'report.user.report_submitted',
    extra: {
      entity_type,
      entity_id,
      reason_id,
      photo_id,
      message_id,
      report_session_id,
    },
  }
}

type UserReportViewScreenEventArgs = {
  screen: string
  report_session_id: string
  entity_id: string
  entity_type: string
  photo_id: string | null
  message_id: string | null
  details: string | null
}

const userReportViewScreenEvent = (args: UserReportViewScreenEventArgs): TrackingEvent => {
  const { screen, report_session_id, entity_id, entity_type, photo_id, message_id, details } = args

  return {
    event: 'report.user.view_screen',
    extra: {
      screen,
      report_session_id,
      entity_id,
      entity_type,
      photo_id,
      message_id,
      details,
    },
  }
}

type ShippingSettingsScreenViewEventArgs = {
  screen: string
}

const shippingSettingsScreenViewEvent = (
  args: ShippingSettingsScreenViewEventArgs,
): TrackingEvent => {
  const { screen } = args

  return {
    event: 'shipping_settings.screen',
    extra: {
      screen,
    },
  }
}

type ShippingSettingsClickEventArgs = {
  screen: string
  target: ClickableElement
  targetDetails?: string
}

type ShippingSettingsClickEventExtra = {
  screen: string
  target: ClickableElement
  target_details?: string
}

const shippingSettingsClickEvent = (args: ShippingSettingsClickEventArgs): TrackingEvent => {
  const { screen, target, targetDetails } = args

  const extra: ShippingSettingsClickEventExtra = {
    screen,
    target,
  }
  if (targetDetails) extra.target_details = targetDetails

  return {
    event: 'shipping_settings.click',
    extra,
  }
}

type ShippingSettingsModifyCarrierPreferencesEventArgs = {
  screen: string
  target: ClickableElement
  targetDetails: string
}

type ShippingSettingsModifyCarrierPreferencesEventExtra = {
  screen: string
  target: ClickableElement
  target_details: string
}

const shippingSettingsModifyCarrierPreferencesEvent = (
  args: ShippingSettingsModifyCarrierPreferencesEventArgs,
): TrackingEvent => {
  const { screen, target, targetDetails } = args

  const extra: ShippingSettingsModifyCarrierPreferencesEventExtra = {
    screen,
    target,
    target_details: targetDetails,
  }

  return {
    event: 'shipping_settings.modify_carrier_preferences',
    extra,
  }
}

type ShippingSettingsCarrierDisableFeedbackEventArgs = {
  screen: string
  carrierCode: string
  carrierDisableReasonsIds: Array<number>
  comment?: string
}

type ShippingSettingsCarrierDisableFeedbackEventExtra = {
  screen: string
  carrier_code: string
  carrier_disable_reasons_ids: Array<number>
  comment?: string
}

const shippingSettingsCarrierDisableFeedbackEvent = (
  args: ShippingSettingsCarrierDisableFeedbackEventArgs,
): TrackingEvent => {
  const { screen, carrierCode, carrierDisableReasonsIds, comment } = args

  const extra: ShippingSettingsCarrierDisableFeedbackEventExtra = {
    screen,
    carrier_code: carrierCode,
    carrier_disable_reasons_ids: carrierDisableReasonsIds,
  }

  if (comment) extra.comment = comment

  return {
    event: 'shipping_settings.carrier_disable_feedback',
    extra,
  }
}

export {
  ViewLandingPageEvent,
  agentSupportFeedbackClosed,
  agentSupportFeedbackV1Viewed,
  authenticateFailEvent,
  authenticateSuccessEvent,
  autoreplyFeedbackClosed,
  autoreplyFeedbackV1Viewed,
  buyerBuyEvent,
  buyerLoadShippingPointsEvent,
  buyerPickDeliveryTypeEvent,
  buyerPrecheckoutEvent,
  buyerPreselectedShippingOptionsEvent,
  buyerViewAddContactDetailsEvent,
  buyerViewCarrierDiscountedShippingPriceEvent,
  buyerViewDeliveryDetailsEvent,
  buyerViewSettingsCheckoutEvent,
  buyerWantItemEvent,
  changeLanguageEvent,
  chatbotFeedbackClosed,
  chatbotFeedbackV1Viewed,
  checkoutBuyerInputEvent,
  checkoutBuyerTimeOnTaskEvent,
  checkoutInteractionEvent,
  checkoutPickPaymentMethodEvent,
  clickCatalogBack,
  clickClaimYourGiftEvent,
  clickClosetPromotionEvent,
  clickEvent,
  clickFilterEvent,
  clickListItemEvent,
  clickPaymentOptionsEvent,
  clickRefundPolicyLinkEvent,
  clickSearchSuggestionAutofillEvent,
  clickSecondaryNavigationEvent,
  confirmWalletEvent,
  contactDetailsClickEvent,
  contactDetailsInputEvent,
  contactDetailsScreenViewEvent,
  continueConversationClicked,
  conversationPasteEvent,
  conversationReplyEvent,
  creditCardAddClickEvent,
  creditCardAddModalLoadDurationEvent,
  dataDomeScriptActionEvent,
  donationsClickEvent,
  dsaNtdUserOpenAppealForm,
  dsaNtdUserSubmitAppealForm,
  dsaUserAddPhotosAppealForm,
  dsaUserOpenAppealAgreement,
  dsaUserOpenAppealEducation,
  dsaUserOpenAppealForm,
  dsaUserSubmitAppealForm,
  endContinueActionsViewedEvent,
  endConversationClicked,
  endOfListEvent,
  favouriteItemEvent,
  firstTimeListerEducationCloseEvent,
  firstTimeListerEducationOpenEvent,
  firstTimeListerEducationPageLeaveEvent,
  followBrandEvent,
  followUserEvent,
  fundraiserClickEvent,
  hasPaymentMethodEvent,
  impressionEvent,
  inboxConversationClickEvent,
  itemAttributesClickEvent,
  itemAttributesViewEvent,
  itemPageChannelClickEvent,
  itemPageChannelViewEvent,
  itemPageViewEvent,
  itemUploadClickEvent,
  itemUploadFillZipCodeFail,
  itemUploadStartEventExtra,
  itemUploadSubmitSuccess,
  itemUploadViewScreenEvent,
  itemUploadViewSimilarSoldItemEvent,
  kycClickEvent,
  kycViewScreenEvent,
  landingPageEvent,
  leaveFaqEntryEvent,
  liveChatBannerClickedEvent,
  liveChatBannerViewedEvent,
  liveChatFormSubmittedEvent,
  liveChatFormViewedEvent,
  makeOfferEvent,
  noticesUserOpenContactForm,
  noticesUserOpenFaq,
  noticesUserSubmitTicket,
  openOfferScreenEvent,
  partialRefundOffersClickEvent,
  paymentOptionsLoadDurationEvent,
  paymentOptionsTimeOnTaskEvent,
  profileClickEvent,
  referralsOpenLinkEvent,
  saveBankAccountEvent,
  scriptDetectionPropertiesEvent,
  sddClickEvent,
  sddViewScreenEvent,
  searchFaqEntriesEvent,
  searchSuggestionAutofillTextSubmittedEvent,
  selectBrandEvent,
  selectCatalogEvent,
  selectSavedSearchEvent,
  selectSearchSuggestionEvent,
  selectSuggestedMessageEvent,
  selectSuggestedPlaceEvent,
  selfServiceViewScreenEvent,
  sellerActionOfferRequest,
  sellerMakeOfferStartEvent,
  sellerViewAddContactDetailsEvent,
  settingsUserInputEvent,
  sharingReferCopyLinkEvent,
  sharingReferralsOpenInfoBannerLink,
  shippingAddressClickEvent,
  shippingAddressInputEvent,
  shippingAddressInteractionEvent,
  shippingAddressScreenViewEvent,
  shippingClickEvent,
  shippingContactInteractionEvent,
  shippingInteractionEvent,
  shippingMapPreselectionEvent,
  shippingScreenViewEvent,
  suggestedShippingPointsCountViewEvent,
  choosePickupPointClickEvent,
  singleCheckoutBuyerInputEvent,
  singleCheckoutCoBrandedCardEvent,
  singleCheckoutCoBrandedCardSelectedEvent,
  singleCheckoutCoBrandedCardSelectionClickEvent,
  singleCheckoutCoBrandedInfoModalFaqEvent,
  singleCheckoutCreditCardAddRegistrationEvent,
  singleCheckoutCvvInfoEvent,
  singleCheckoutTokenizationOutcomeEvent,
  sizeFilterEvent,
  startConversationEvent,
  startMakeOfferRequestEvent,
  sustainabilityImpactViewEvent,
  systemTimingEvent,
  taxRulesPlacementClickEvent,
  taxpayerTimeOnTaskEvent,
  taxpayersFormForceTriggerEvent,
  toggleSearchSubscriptionEvent,
  transactionSellerInputEvent,
  updatePriceFromConversationEvent,
  updatePriceFromConversationViewEvent,
  userAppliedOrderEvent,
  userCarrierDisableFeedbackEvent,
  userClickDynamicFilterOptionEvent,
  userClickFaqRelatedQuestionEvent,
  userClickHelpCenterSearchResult,
  userClickHomepageBlockCta,
  userClickHomepageElement,
  userClickHomepageVertical,
  userClickMemberEvent,
  userClickRecommendedTopicEvent,
  userContactSupportEvent,
  userFilterItemsEvent,
  userInputEvent,
  userKybSuccessEvent,
  userMessageThreadBannerClickLinkEvent,
  userMessageThreadBannerCloseEvent,
  userMessageThreadBannerViewEvent,
  userModifyCarrierPreferencesEvent,
  userPaginateCatalogEvent,
  userRegisterFailEvent,
  userRegisterSuccessEvent,
  userSelectTheme,
  userSubmitFaqFeedbackEvent,
  userViewCheckoutEvent,
  userViewDynamicFilterOptionEvent,
  userViewHomepageBlock,
  userViewHomepageBlockCta,
  userViewHomepageElement,
  userViewItemGallery,
  viewCreditCardAddEvent,
  viewEvent,
  viewFaqEntry,
  viewFilterEvent,
  viewItemEvent,
  viewOwnItemEvent,
  viewPaymentOptionsEvent,
  viewSavedSearchEvent,
  viewScreenEvent,
  viewSearchSuggestionEvent,
  viewSearchSuggestionsEvent,
  viewSecondaryNavigationEvent,
  viewShippingPriceEvent,
  viewSuggestedMessageEvent,
  viewSuggestedPlacesEvent,
  viewUserEvent,
  walletHistoryTimeOnTaskEvent,
  cannotBundleButtonViewEvent,
  cannotBundleButtonClickEvent,
  cannotBundleHyperlinkClickEvent,
  creditCardAddModalTaskDurationEvent,
  taxpayersClickEvent,
  taxpayersInputEvent,
  taxpayersViewEvent,
  taxpayersViewScreenEvent,
  escrowOrderDetailsViewEvent,
  escrowOrderDetailsActionLatencyViewEvent,
  escrowOrderDetailsClickEvent,
  userReportSubmittedEvent,
  userReportViewScreenEvent,
  shippingSettingsScreenViewEvent,
  shippingSettingsClickEvent,
  shippingSettingsModifyCarrierPreferencesEvent,
  shippingSettingsCarrierDisableFeedbackEvent,
}
